import { useState, useEffect, useCallback, useRef } from "react";

// ────────────────────────────────────────────────────────────────
//  STORAGE  (localStorage, 배포 환경 정상 작동)
// ────────────────────────────────────────────────────────────────
const db = {
  get(k)    { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : null; } catch { return null; } },
  set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} },
};

// ────────────────────────────────────────────────────────────────
//  CONSTANTS
// ────────────────────────────────────────────────────────────────
const EQUIP_TYPES = ["밸브", "스프링클러", "QC", "제어밸브", "기타"];
const HOLES       = [...Array(18)].map((_,i)=>String(i+1)).concat(["연습장","클럽하우스"]);
const ZONES       = ["Fairway","Green","Tee","Rough","Bunker","Entrance","기타"];
const WORK_TYPES  = ["점검","청소","부품 교체","수리","조정","기타"];
const uid         = () => Math.random().toString(36).slice(2,9);

// ── POGO BLE
const POGO_SERVICE = "6e400001-b5a3-f393-e0a9-e50e24dcca9e";
const POGO_RX      = "6e400002-b5a3-f393-e0a9-e50e24dcca9e";
const POGO_TX      = "6e400003-b5a3-f393-e0a9-e50e24dcca9e";

function moistureStatus(v, threshold) {
  const t = threshold || { dry:15, caution:25, wet:45 };
  const n = Number(v);
  if (n < t.dry)     return { label:"건조", color:"#ef4444", bg:"#450a0a", border:"#7f1d1d" };
  if (n < t.caution) return { label:"주의", color:"#fbbf24", bg:"#1c1003", border:"#92400e" };
  if (n < t.wet)     return { label:"적정", color:"#4ade80", bg:"#022c22", border:"#065f46" };
  return                    { label:"과습", color:"#60a5fa", bg:"#0c1a3b", border:"#1e40af" };
}

const DEFAULT_GC = { name:"", address:"", lat:"", lng:"", courses:[] };

// 코스·홀 표시 헬퍼
const holeLabel = (eq) => eq.course ? `${eq.course} ${eq.hole}번홀` : `${eq.hole}번홀`;

// ────────────────────────────────────────────────────────────────
//  DEMO DATA
// ────────────────────────────────────────────────────────────────
const DEMO = {
  equipment: [
    { id:"eq1", type:"밸브",      code:"V-001", hole:"9",  zone:"Fairway", lat:37.190802, lng:127.581252, nfc:"NFC-001", notes:"버마드 밸브 · 디코더 MK3", installed:"2026-02-10", photos:[] },
    { id:"eq2", type:"밸브",      code:"V-002", hole:"9",  zone:"Green",   lat:37.191312, lng:127.581464, nfc:"NFC-002", notes:"버마드 밸브 · 디코더 MK3", installed:"2022-03-10", photos:[] },
    { id:"eq3", type:"스프링클러",code:"S-001", hole:"9",  zone:"Fairway", lat:37.190808, lng:127.581381, nfc:"",        notes:"레인버드 752B · 반경 30m",  installed:"2022-03-15", photos:[] },
    { id:"eq4", type:"QC",        code:"QC-001",hole:"9",  zone:"Fairway", lat:37.190838, lng:127.581104, nfc:"",        notes:"페어웨이 중앙",             installed:"2022-04-01", photos:[] },
    { id:"eq5", type:"제어밸브",  code:"CV-001",hole:"9",  zone:"Green",   lat:37.191400, lng:127.581351, nfc:"NFC-005", notes:"",                         installed:"2021-11-20", photos:[] },
  ],
  repairs: [
    { id:"r1", eqId:"eq1", date:"2025-01-15", worker:"김철수", type:"점검",     parts:"",        notes:"정상 작동 확인", photos:[] },
    { id:"r2", eqId:"eq3", date:"2025-02-20", worker:"이영희", type:"부품 교체", parts:"노즐 #3", notes:"마모로 교체",   photos:[] },
    { id:"r3", eqId:"eq4", date:"2025-03-01", worker:"박민준", type:"청소",     parts:"필터",    notes:"이물질 제거",   photos:[] },
  ],
};

// ────────────────────────────────────────────────────────────────
//  ICONS
// ────────────────────────────────────────────────────────────────
const Ic = {
  find:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>,
  map:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3V6z"/><path d="M9 3v15M15 6v15"/></svg>,
  manage:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2M9 5h6"/><path d="M9 12h6M9 16h4"/></svg>,
  nfc:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 5C3.24 7.03 2 10 2 12s1.24 4.97 4 7"/><path d="M10 8.35C8.52 9.48 8 10.79 8 12s.52 2.52 2 3.65"/><circle cx="12" cy="12" r="2"/><path d="M14 8.35c1.48 1.13 2 2.44 2 3.65s-.52 2.52-2 3.65"/><path d="M18 5c2.76 2.03 4 5 4 7s-1.24 4.97-4 7"/></svg>,
  gps:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/></svg>,
  back:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>,
  plus:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  edit:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"/></svg>,
  trash:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>,
  repair:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
  pin:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  home:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  camera:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>,
  image:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>,
  x:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  expand:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>,
  settings:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  golf:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="18" r="3"/><path d="M12 15V5"/><path d="M12 5l6 3-6 3"/></svg>,
  leaf:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/></svg>,
  share:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>,
};

// ────────────────────────────────────────────────────────────────
//  TYPE STYLE
// ────────────────────────────────────────────────────────────────
const TYPE_STYLE = {
  "밸브":      { color:"#34d399", bg:"#022c22", border:"#065f46" },
  "스프링클러":{ color:"#60a5fa", bg:"#0c1a3b", border:"#1e40af" },
  "QC":        { color:"#fbbf24", bg:"#1c1003", border:"#92400e" },
  "제어밸브":  { color:"#f87171", bg:"#1c0505", border:"#7f1d1d" },
  "기타":      { color:"#a78bfa", bg:"#1e1033", border:"#5b21b6" },
};

function TypeBadge({ type, small }) {
  const s = TYPE_STYLE[type] || TYPE_STYLE["기타"];
  return (
    <span style={{ display:"inline-block", padding:small?"2px 8px":"3px 10px", borderRadius:20, fontSize:small?10:11,
      fontWeight:600, color:s.color, background:s.bg, border:`1px solid ${s.border}` }}>{type}</span>
  );
}

// ────────────────────────────────────────────────────────────────
//  SHARED UI
// ────────────────────────────────────────────────────────────────
function TopBar({ title, onBack, onHome, right }) {
  return (
    <div style={{ position:"sticky", top:0, zIndex:50, background:"#0a0f0a",
      borderBottom:"1px solid #1a2e1a", display:"flex", alignItems:"center", gap:10, padding:"13px 16px" }}>
      {onBack && (
        <button onClick={onBack} style={{ background:"transparent", border:"1px solid #1a2e1a",
          borderRadius:8, padding:"7px 9px", color:"#4ade80", cursor:"pointer", display:"flex" }}>{Ic.back}</button>
      )}
      <span style={{ flex:1, fontSize:16, fontWeight:700, color:"#f0fdf4", fontFamily:"'Noto Sans KR',sans-serif" }}>{title}</span>
      {onHome && (
        <button onClick={onHome} style={{ background:"transparent", border:"1px solid #1a2e1a",
          borderRadius:8, padding:"7px 9px", color:"#4ade80", cursor:"pointer", display:"flex" }}>{Ic.home}</button>
      )}
      {right}
    </div>
  );
}

function BottomTab({ active, go }) {
  const tabs = [
    { id:"home",   label:"홈",   icon:Ic.home },
    { id:"find",   label:"찾기", icon:Ic.find },
    { id:"map",    label:"지도", icon:Ic.map },
    { id:"manage", label:"관리", icon:Ic.manage },
    { id:"soil",   label:"토양", icon:Ic.leaf },
  ];
  return (
    <div style={{ position:"fixed", bottom:0, left:0, right:0, background:"#0a0f0a",
      borderTop:"1px solid #1a2e1a", display:"flex", zIndex:100 }}>
      {tabs.map(t => (
        <button key={t.id} onClick={()=>go(t.id)}
          style={{ flex:1, padding:"12px 4px 16px", background:"transparent", border:"none",
            cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:4,
            color:active===t.id?"#4ade80":"#9ca3af", transition:"color .15s" }}>
          <span style={{ width:22, height:22 }}>{t.icon}</span>
          <span style={{ fontSize:10, fontFamily:"'Noto Sans KR',sans-serif", letterSpacing:".02em" }}>{t.label}</span>
        </button>
      ))}
    </div>
  );
}

function Inp({ label, ...props }) {
  return (
    <div>
      {label && <label style={{ display:"block", color:"#6b7280", fontSize:12, marginBottom:5, letterSpacing:".04em" }}>{label}</label>}
      <input {...props} style={{ width:"100%", background:"#0d150d", border:"1px solid #1a2e1a", borderRadius:8,
        padding:"10px 14px", color:"#e2e8f0", fontFamily:"inherit", fontSize:14, outline:"none",
        transition:"border-color .15s", ...(props.style||{}) }}
        onFocus={e=>e.target.style.borderColor="#4ade80"}
        onBlur={e=>e.target.style.borderColor="#1a2e1a"} />
    </div>
  );
}

function Sel({ label, children, ...props }) {
  return (
    <div>
      {label && <label style={{ display:"block", color:"#6b7280", fontSize:12, marginBottom:5, letterSpacing:".04em" }}>{label}</label>}
      <select {...props} style={{ width:"100%", background:"#0d150d", border:"1px solid #1a2e1a", borderRadius:8,
        padding:"10px 14px", color:"#e2e8f0", fontFamily:"inherit", fontSize:14, outline:"none" }}>
        {children}
      </select>
    </div>
  );
}

function Btn({ children, variant="green", style={}, ...props }) {
  const styles = {
    green:  { background:"#14532d", border:"1px solid #22c55e", color:"#4ade80" },
    ghost:  { background:"transparent", border:"1px solid #1a2e1a", color:"#6b7280" },
    danger: { background:"#450a0a", border:"1px solid #ef4444", color:"#f87171" },
    blue:   { background:"#0c1a3b", border:"1px solid #3b82f6", color:"#60a5fa" },
  };
  return (
    <button {...props} style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", gap:6,
      padding:"10px 18px", borderRadius:8, cursor:"pointer", fontFamily:"inherit", fontSize:14,
      fontWeight:600, transition:"all .15s", ...styles[variant], ...style }}>{children}</button>
  );
}

function Card({ children, style={}, ...props }) {
  return <div style={{ background:"#0d150d", border:"1px solid #1a2e1a", borderRadius:12, ...style }} {...props}>{children}</div>;
}

// ────────────────────────────────────────────────────────────────
//  GPS FIELD
// ────────────────────────────────────────────────────────────────
function GpsField({ lat, lng, onChange }) {
  const [loading, setLoading] = useState(false);
  const capture = () => {
    setLoading(true);
    navigator.geolocation?.getCurrentPosition(
      p => { onChange(p.coords.latitude.toFixed(6), p.coords.longitude.toFixed(6)); setLoading(false); },
      () => { window.alert("GPS를 사용할 수 없습니다"); setLoading(false); },
      { enableHighAccuracy:true }
    );
  };
  return (
    <Card style={{ padding:14 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
        <label style={{ color:"#6b7280", fontSize:12, letterSpacing:".04em", display:"flex", alignItems:"center", gap:6 }}>
          <span style={{ width:14, height:14, color:"#4ade80" }}>{Ic.gps}</span> GPS 좌표
        </label>
        <Btn variant="green" style={{ padding:"6px 12px", fontSize:12 }} onClick={capture} disabled={loading}>
          {loading ? "확인중..." : "현재 위치 등록"}
        </Btn>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
        <Inp placeholder="위도 (Lat)" value={lat||""} onChange={e=>onChange(e.target.value, lng)} />
        <Inp placeholder="경도 (Lng)" value={lng||""} onChange={e=>onChange(lat, e.target.value)} />
      </div>
      {lat && lng && (
        <p style={{ color:"#4ade80", fontSize:11, marginTop:8, textAlign:"center" }}>
          ✓ {Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}
        </p>
      )}
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────
//  📸 PHOTO CAPTURE
// ────────────────────────────────────────────────────────────────
function PhotoCapture({ photos=[], onChange, max=5, label="사진" }) {
  const cameraRef  = useRef(null);
  const galleryRef = useRef(null);
  const [preview, setPreview] = useState(null);

  const readFile = file => new Promise(res => {
    const r = new FileReader();
    r.onload = e => res(e.target.result);
    r.readAsDataURL(file);
  });

  const handleFiles = async files => {
    const added = await Promise.all(Array.from(files).map(readFile));
    onChange([...photos, ...added].slice(0, max));
  };

  const remove = idx => {
    if (!window.confirm("사진을 삭제하시겠습니까?")) return;
    onChange(photos.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <label style={{ display:"block", color:"#6b7280", fontSize:12, marginBottom:8, letterSpacing:".04em" }}>
        {label} ({photos.length}/{max})
      </label>
      <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
        {photos.map((src, i) => (
          <div key={i} style={{ position:"relative", width:80, height:80 }}>
            <img src={src} alt={`사진${i+1}`} onClick={()=>setPreview(src)}
              style={{ width:80, height:80, objectFit:"cover", borderRadius:10,
                border:"1px solid #1a2e1a", cursor:"pointer" }} />
            <button onClick={()=>remove(i)}
              style={{ position:"absolute", top:-6, right:-6, width:20, height:20,
                background:"#450a0a", border:"1px solid #ef4444", borderRadius:"50%",
                color:"#f87171", cursor:"pointer", display:"flex", alignItems:"center",
                justifyContent:"center", padding:0 }}>
              <span style={{ width:10, height:10 }}>{Ic.x}</span>
            </button>
            <div onClick={()=>setPreview(src)}
              style={{ position:"absolute", bottom:4, right:4, width:18, height:18,
                background:"rgba(0,0,0,.6)", borderRadius:4, display:"flex",
                alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
              <span style={{ width:10, height:10, color:"#fff" }}>{Ic.expand}</span>
            </div>
          </div>
        ))}
        {photos.length < max && (
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            <button onClick={()=>cameraRef.current?.click()}
              style={{ width:80, height:36, background:"#0d150d", border:"1px dashed #166534",
                borderRadius:8, color:"#4ade80", cursor:"pointer", display:"flex",
                alignItems:"center", justifyContent:"center", gap:4, fontSize:11 }}>
              <span style={{ width:14, height:14 }}>{Ic.camera}</span> 촬영
            </button>
            <button onClick={()=>galleryRef.current?.click()}
              style={{ width:80, height:36, background:"#0d150d", border:"1px dashed #1e40af",
                borderRadius:8, color:"#60a5fa", cursor:"pointer", display:"flex",
                alignItems:"center", justifyContent:"center", gap:4, fontSize:11 }}>
              <span style={{ width:14, height:14 }}>{Ic.image}</span> 갤러리
            </button>
          </div>
        )}
      </div>
      <input ref={cameraRef}  type="file" accept="image/*" capture="environment"
        style={{ display:"none" }} onChange={e=>handleFiles(e.target.files)} />
      <input ref={galleryRef} type="file" accept="image/*" multiple
        style={{ display:"none" }} onChange={e=>handleFiles(e.target.files)} />
      {preview && (
        <div onClick={()=>setPreview(null)}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.92)", zIndex:9999,
            display:"flex", alignItems:"center", justifyContent:"center" }}>
          <img src={preview} alt="미리보기"
            style={{ maxWidth:"94vw", maxHeight:"88vh", objectFit:"contain", borderRadius:12 }} />
          <button onClick={()=>setPreview(null)}
            style={{ position:"absolute", top:20, right:20, background:"rgba(0,0,0,.7)",
              border:"1px solid #374151", borderRadius:"50%", width:40, height:40,
              color:"#e2e8f0", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span style={{ width:18, height:18 }}>{Ic.x}</span>
          </button>
          <p style={{ position:"absolute", bottom:24, color:"#4b5563", fontSize:12 }}>탭하면 닫힘</p>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
//  📸 PHOTO STRIP (읽기 전용)
// ────────────────────────────────────────────────────────────────
function PhotoStrip({ photos=[] }) {
  const [preview, setPreview] = useState(null);
  if (!photos || photos.length === 0) return null;
  return (
    <>
      <div style={{ display:"flex", gap:8, overflowX:"auto", paddingBottom:4 }}>
        {photos.map((src, i) => (
          <img key={i} src={src} alt={`사진${i+1}`} onClick={()=>setPreview(src)}
            style={{ width:80, height:80, objectFit:"cover", borderRadius:10,
              border:"1px solid #1a2e1a", flexShrink:0, cursor:"pointer" }} />
        ))}
      </div>
      {preview && (
        <div onClick={()=>setPreview(null)}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.92)", zIndex:9999,
            display:"flex", alignItems:"center", justifyContent:"center" }}>
          <img src={preview} alt="미리보기"
            style={{ maxWidth:"94vw", maxHeight:"88vh", objectFit:"contain", borderRadius:12 }} />
          <button onClick={()=>setPreview(null)}
            style={{ position:"absolute", top:20, right:20, background:"rgba(0,0,0,.7)",
              border:"1px solid #374151", borderRadius:"50%", width:40, height:40,
              color:"#e2e8f0", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span style={{ width:18, height:18 }}>{Ic.x}</span>
          </button>
          <p style={{ position:"absolute", bottom:24, color:"#4b5563", fontSize:12 }}>탭하면 닫힘</p>
        </div>
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────
//  DISTANCE HELPER
// ────────────────────────────────────────────────────────────────
function calcDist(lat1, lng1, lat2, lng2) {
  return Math.round(Math.sqrt(Math.pow((lat2-lat1)*111000,2)+Math.pow((lng2-lng1)*111000*Math.cos(lat1*Math.PI/180),2)));
}
function calcDir(lat1, lng1, lat2, lng2) {
  const a = (Math.atan2(lng2-lng1, lat2-lat1)*180/Math.PI+360)%360;
  return ["↑N","↗NE","→E","↘SE","↓S","↙SW","←W","↖NW"][Math.round(a/45)%8];
}

// ════════════════════════════════════════════════════════════════
//  APP ROOT
// ════════════════════════════════════════════════════════════════
export default function App() {
  const [equipment,   setEquipment]   = useState([]);
  const [repairs,     setRepairs]     = useState([]);
  const [soilData,    setSoilData]    = useState([]);   // 토양 측정 기록
  const [soilThreshold, setSoilThreshold] = useState(() =>
    db.get("soilThreshold") || { dry:15, caution:25, wet:45 });
  const [golfCourses, setGolfCourses] = useState([]);   // 골프장 배열
  const [activeGCId,  setActiveGCId]  = useState(null); // 현재 활성 골프장 id
  const [loaded,      setLoaded]      = useState(false);
  const [tab,         setTab]         = useState("home");
  const [manageSort,  setManageSort]  = useState("default");
  const [screen,      setScreen]      = useState(null);
  const [ctx,         setCtx]         = useState({});
  const screenRef = useRef(null);

  // ── 데이터 로드 (기존 단일 golfcourse 마이그레이션 포함)
  useEffect(() => {
    const eq   = db.get("equipment");
    const rep  = db.get("repairs");
    const soil = db.get("soildata");
    let gcs    = db.get("golfcourses");   // 새 키 (배열)
    let actId = db.get("activegcid");

    // 마이그레이션: 구 버전 단일 golfcourse → 배열로 변환
    if (!gcs) {
      const old = db.get("golfcourse");
      if (old && old.name) {
        const migrated = { ...old, id: old.id || uid() };
        gcs   = [migrated];
        actId = migrated.id;
        db.set("golfcourses", gcs);
        db.set("activegcid",  actId);
      } else {
        gcs = [];
      }
    }

    setEquipment(eq   || DEMO.equipment);
    setRepairs(rep    || DEMO.repairs);
    setSoilData(soil  || []);
    setGolfCourses(gcs);
    setActiveGCId(actId || gcs[0]?.id || null);
    setLoaded(true);
  }, []);

  // ── 뒤로가기 처리
  useEffect(() => {
    window.history.replaceState({ valve:"root" }, "");
    window.history.pushState(  { valve:"root" }, "");
  }, []);

  useEffect(() => { screenRef.current = screen; }, [screen]);

  useEffect(() => {
    const handlePop = () => {
      if (screenRef.current) {
        setScreen(null); setCtx({});
        window.history.pushState({ valve:"root" }, "");
      } else {
        window.history.pushState({ valve:"root" }, "");
      }
    };
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []);

  const saveEq       = useCallback(d => { setEquipment(d);  db.set("equipment",  d); }, []);
  const saveRep      = useCallback(d => { setRepairs(d);    db.set("repairs",    d); }, []);
  const saveSoilData = useCallback(d => { setSoilData(d);   db.set("soildata",   d); }, []);
  const saveSoilThreshold = (v) => { setSoilThreshold(v); db.set("soilThreshold", v); };

  const saveGCs = useCallback(d => {
    setGolfCourses(d);
    db.set("golfcourses", d);
  }, []);

  const saveActiveGCId = useCallback(id => {
    setActiveGCId(id);
    db.set("activegcid", id);
  }, []);

  const push = (s, c={}) => { window.history.pushState({ valve:s }, ""); setScreen(s); setCtx(c); };
  const pop  = ()         => { setScreen(null); setCtx({}); };

  if (!loaded) return (
    <div style={{ background:"#0a0f0a", minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ color:"#4ade80", fontSize:14, fontFamily:"monospace", letterSpacing:".1em" }}>LOADING...</div>
    </div>
  );

  // 현재 활성 골프장 (없으면 첫 번째, 그것도 없으면 빈 객체)
  const activeGC = golfCourses.find(gc => gc.id === activeGCId) || golfCourses[0] || DEFAULT_GC;

  const shared    = { equipment, repairs, saveEq, saveRep, push, pop };
  const gcShared  = { golfCourses, saveGCs, activeGCId, saveActiveGCId };
  const onGCList  = () => push("gcList");

  return (
    <div style={{ background:"transparent", minHeight:"100vh", fontFamily:"'Noto Sans KR','DM Mono',monospace",
      color:"#e2e8f0", maxWidth:480, margin:"0 auto" }}>
      <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;500;700&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet"/>
      <style>{`
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:3px}::-webkit-scrollbar-thumb{background:#1a2e1a;border-radius:2px}
        input,select,textarea,button{font-family:inherit}
        textarea{outline:none}
        .slide-up{animation:su .22s ease}
        @keyframes su{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
      `}</style>

      {!screen && (
        <>
          {tab==="home"   && <HomeScreen   {...shared} activeGC={activeGC} golfCourses={golfCourses} goTab={setTab}
              goManage={sort=>{setManageSort(sort);setTab("manage");}} onGCList={onGCList} />}
          {tab==="find"   && <FindScreen   {...shared} />}
          {tab==="map"    && <MapScreen    {...shared} golfCourse={activeGC} />}
          {tab==="manage" && <ManageScreen {...shared} sortBy={manageSort} onGCList={onGCList} />}
          {tab==="soil"   && <SoilScreen   soilData={soilData} saveSoilData={saveSoilData} activeGC={activeGC}
                               soilThreshold={soilThreshold} saveSoilThreshold={saveSoilThreshold} />}
          <BottomTab active={tab} go={t=>setTab(t)} />
        </>
      )}

      {screen==="detail"     && <DetailScreen    {...shared} eq={ctx.eq} goHome={()=>{setScreen(null);setTab("home");}} />}
      {screen==="eqForm"     && <EqFormScreen    {...shared} eq={ctx.eq} golfCourse={activeGC} goHome={()=>{setScreen(null);setTab("home");}} />}
      {screen==="repairForm" && <RepairFormScreen {...shared} repair={ctx.repair} eqId={ctx.eqId} goHome={()=>{setScreen(null);setTab("home");}} />}
      {screen==="gcList"     && <GolfCourseListScreen {...gcShared} push={push} pop={pop} />}
      {screen==="gcForm"     && <GolfCourseFormScreen {...gcShared} gc={ctx.gc} pop={pop} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  HOME SCREEN
// ════════════════════════════════════════════════════════════════
function HomeScreen({ equipment, repairs, activeGC, golfCourses, goTab, goManage, onGCList }) {
  const today    = new Date();
  const dateStr  = today.toLocaleDateString("ko-KR", { year:"numeric", month:"long", day:"numeric", weekday:"long" });
  const totalEq  = equipment.length;
  const totalRep = repairs.length;
  const thisMonth = repairs.filter(r => r.date?.slice(0,7) === today.toISOString().slice(0,7)).length;

  const hasGC  = !!(activeGC?.name);
  const gcName = hasGC ? activeGC.name : "골프장 미등록";
  const gcCount = golfCourses.length;

  const menuColors = [
    { id:"find",   icon:Ic.find,   label:"밸브 찾기",  desc:"NFC · QR · GPS · 코드 검색",  color:"#16a34a", bg:"#f0fdf4", border:"#bbf7d0", shadow:"rgba(22,163,74,.12)"   },
    { id:"map",    icon:Ic.map,    label:"지도 보기",  desc:"전체 장비 위치 확인",           color:"#2563eb", bg:"#eff6ff", border:"#bfdbfe", shadow:"rgba(37,99,235,.12)"  },
    { id:"manage", icon:Ic.manage, label:"장비 관리",  desc:"등록 · 수정 · 수리 기록",       color:"#d97706", bg:"#fffbeb", border:"#fde68a", shadow:"rgba(217,119,6,.12)"  },
  ];

  return (
    <div className="slide-up" style={{ minHeight:"100vh", paddingBottom:80, background:"#ffffff" }}>

      {/* ── 상단 헤더 */}
      <div style={{ padding:"28px 20px 20px", background:"linear-gradient(180deg,#f8fafc 0%,#ffffff 100%)",
        borderBottom:"1px solid #e5e7eb" }}>

        {/* 골프장명 + 설정 버튼 */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <img src="/logo_goldentree.png" alt="Golden Tree"
              style={{ width:64, height:64, borderRadius:10, objectFit:"cover", flexShrink:0 }}
              onError={e=>{e.target.style.display="none";}} />
            <img src="/logo_valvefinder1.png" alt="Valve Finder"
              style={{ height:44, maxWidth:180, objectFit:"contain" }}
              onError={e=>{e.target.style.display="none";}} />
          </div>
          <button onClick={onGCList}
            style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 12px",
              background: hasGC ? "#f0fdf4" : "#fefce8",
              border: `1px solid ${hasGC ? "#bbf7d0" : "#fde68a"}`,
              borderRadius:20, cursor:"pointer", fontSize:12,
              color: hasGC ? "#16a34a" : "#d97706", fontWeight:600 }}>
            <span style={{ width:14, height:14 }}>{Ic.golf}</span>
            {hasGC ? gcName : "골프장 등록"}
            {gcCount > 1 && <span style={{ background:"#bbf7d0", color:"#166534", borderRadius:10,
              padding:"1px 6px", fontSize:10, fontWeight:700 }}>{gcCount}</span>}
          </button>
        </div>

        {/* 골프장명 표시 */}
        {hasGC ? (
          <div style={{ marginBottom:8 }}>
            <p style={{ color:"#6b7280", fontSize:11, letterSpacing:".12em", fontWeight:700 }}>GOLF COURSE</p>
            <p style={{ color:"#1e293b", fontSize:20, fontWeight:800, marginTop:2 }}>{gcName}</p>
            {activeGC?.courses?.length > 0 && (
              <p style={{ color:"#9ca3af", fontSize:11, marginTop:3 }}>
                {activeGC.courses.map(c=>c.name).join(" · ")}
              </p>
            )}
          </div>
        ) : (
          <button onClick={onGCList}
            style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"12px 14px",
              background:"#fffbeb", border:"1px dashed #fde68a", borderRadius:12,
              cursor:"pointer", marginBottom:8, textAlign:"left" }}>
            <span style={{ color:"#d97706", fontSize:20 }}>⛳</span>
            <div>
              <p style={{ color:"#d97706", fontSize:13, fontWeight:700 }}>골프장을 등록하세요</p>
              <p style={{ color:"#9ca3af", fontSize:11, marginTop:2 }}>이름 · 지도 중심 좌표 설정</p>
            </div>
          </button>
        )}

        <p style={{ color:"#9ca3af", fontSize:11, letterSpacing:".04em" }}>{dateStr}</p>
      </div>

      {/* ── 요약 통계 */}
      <div style={{ padding:"20px 20px 16px", display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
        {[
          { label:"전체 장비", value:totalEq,   color:"#16a34a", bg:"#f0fdf4", border:"#bbf7d0", sort:"default"   },
          { label:"수리 기록", value:totalRep,  color:"#2563eb", bg:"#eff6ff", border:"#bfdbfe", sort:"repair"    },
          { label:"이번달",    value:thisMonth, color:"#d97706", bg:"#fffbeb", border:"#fde68a", sort:"thismonth" },
        ].map(s => (
          <div key={s.label} onClick={()=>goManage(s.sort)}
            style={{ background:s.bg, border:`1px solid ${s.border}`,
              borderRadius:14, padding:"14px 10px", textAlign:"center",
              boxShadow:"0 1px 4px rgba(0,0,0,.06)", cursor:"pointer" }}>
            <p style={{ color:s.color, fontSize:24, fontWeight:800, lineHeight:1 }}>{s.value}</p>
            <p style={{ color:"#6b7280", fontSize:10, marginTop:5, fontWeight:500 }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* ── 메인 메뉴 */}
      <div style={{ padding:"4px 20px", display:"flex", flexDirection:"column", gap:12 }}>
        {menuColors.map(m => (
          <button key={m.id} onClick={()=>goTab(m.id)}
            style={{ width:"100%", background:m.bg, border:`1.5px solid ${m.border}`, borderRadius:18,
              padding:"20px", cursor:"pointer", display:"flex", alignItems:"center", gap:16,
              transition:"all .2s", textAlign:"left", boxShadow:`0 2px 12px ${m.shadow}` }}
            onMouseEnter={e=>{e.currentTarget.style.boxShadow=`0 4px 20px ${m.shadow}`;e.currentTarget.style.transform="translateY(-1px)";}}
            onMouseLeave={e=>{e.currentTarget.style.boxShadow=`0 2px 12px ${m.shadow}`;e.currentTarget.style.transform="translateY(0)";}}>
            <div style={{ width:52, height:52, background:"#fff", border:`1.5px solid ${m.border}`,
              borderRadius:14, display:"flex", alignItems:"center", justifyContent:"center",
              color:m.color, flexShrink:0, boxShadow:`0 2px 8px ${m.shadow}` }}>
              <span style={{ width:26, height:26 }}>{m.icon}</span>
            </div>
            <div style={{ flex:1 }}>
              <p style={{ color:m.color, fontSize:18, fontWeight:700, marginBottom:3, fontFamily:"'Noto Sans KR',sans-serif" }}>{m.label}</p>
              <p style={{ color:"#9ca3af", fontSize:13 }}>{m.desc}</p>
            </div>
            <span style={{ color:m.border, fontSize:22, fontWeight:300 }}>›</span>
          </button>
        ))}
      </div>

      <p style={{ textAlign:"center", color:"#d1d5db", fontSize:11, marginTop:32, letterSpacing:".08em", paddingBottom:8 }}>
        VALVE FINDER v1.0
      </p>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  FIND SCREEN
// ════════════════════════════════════════════════════════════════
function FindScreen({ equipment, push }) {
  const [query,   setQuery]   = useState("");
  const [userPos, setUserPos] = useState(null);
  const [nearest, setNearest] = useState(null);
  const [nfcMsg,  setNfcMsg]  = useState("");
  const [qrActive, setQrActive] = useState(false);
  const [qrMsg,    setQrMsg]    = useState("");
  const qrScanner = useRef(null);

  const stopQR = () => {
    if (qrScanner.current) { qrScanner.current.stop().catch(()=>{}); qrScanner.current = null; }
    setQrActive(false);
  };

  const scanQR = () => {
    setQrActive(true); setQrMsg("");
    const load = () => {
      const Html5Qrcode = window.Html5Qrcode;
      if (!Html5Qrcode) return setTimeout(load, 200);
      const scanner = new Html5Qrcode("qr-reader");
      qrScanner.current = scanner;
      scanner.start(
        { facingMode:"environment" }, { fps:10, qrbox:250 },
        text => {
          stopQR();
          const found = equipment.find(e => e.code===text || e.nfc===text);
          if (found) push("detail", { eq:found });
          else setQrMsg(`QR 코드: ${text} — 등록되지 않은 장비입니다`);
        },
        () => {}
      ).catch(() => setQrMsg("카메라 권한을 허용해 주세요"));
    };
    if (!window.Html5Qrcode) {
      const js = document.createElement("script");
      js.src = "https://unpkg.com/html5-qrcode@2.3.8/html5-qrcode.min.js";
      js.onload = load;
      document.head.appendChild(js);
    } else load();
  };

  useEffect(() => {
    const id = navigator.geolocation?.watchPosition(
      p => {
        const pos = { lat:p.coords.latitude, lng:p.coords.longitude };
        setUserPos(pos);
        const eqWithGps = equipment.filter(e => e.lat && e.lng);
        if (eqWithGps.length > 0) {
          let min=Infinity, near=null;
          eqWithGps.forEach(e => {
            const d = calcDist(pos.lat, pos.lng, Number(e.lat), Number(e.lng));
            if (d < min) { min=d; near=e; }
          });
          setNearest({ eq:near, dist:min, dir:calcDir(pos.lat, pos.lng, Number(near.lat), Number(near.lng)) });
        }
      },
      null, { enableHighAccuracy:true }
    );
    return () => { if (id) navigator.geolocation?.clearWatch(id); };
  }, [equipment]);

  const scanNFC = async () => {
    if (!("NDEFReader" in window)) { setNfcMsg("이 기기는 NFC를 지원하지 않습니다"); setTimeout(()=>setNfcMsg(""),3000); return; }
    try {
      const reader = new window.NDEFReader();
      await reader.scan();
      setNfcMsg("📡 NFC 태그에 가져다 대세요...");
      reader.onreading = ({ message }) => {
        const text = new TextDecoder().decode(message.records[0].data);
        const found = equipment.find(e => e.nfc===text);
        if (found) { push("detail", { eq:found }); setNfcMsg(""); }
        else setNfcMsg(`태그 ID: ${text} — 등록되지 않은 장비입니다`);
      };
    } catch { setNfcMsg("NFC 스캔 실패. 권한을 확인하세요."); setTimeout(()=>setNfcMsg(""),3000); }
  };

  const results = query.length > 0
    ? equipment.filter(e =>
        e.code.toLowerCase().includes(query.toLowerCase()) ||
        e.type.includes(query) ||
        e.notes?.toLowerCase().includes(query.toLowerCase())
      )
    : [];

  return (
    <div className="slide-up" style={{ paddingBottom:80, minHeight:"100vh" }}>
      <div style={{ padding:"20px 16px 16px" }}>
        <p style={{ color:"#374151", fontSize:11, letterSpacing:".1em", marginBottom:4 }}>GOLF COURSE</p>
        <h1 style={{ fontSize:26, fontWeight:700, color:"#14532d", letterSpacing:"-.02em" }}>밸브 찾기</h1>
      </div>

      {/* NFC 스캔 */}
      <div style={{ padding:"0 16px 20px" }}>
        <button onClick={scanNFC}
          style={{ width:"100%", background:"linear-gradient(135deg,#052e16 0%,#0f3a1f 100%)",
            border:"2px solid #22c55e", borderRadius:16, padding:"24px 20px", cursor:"pointer",
            display:"flex", alignItems:"center", gap:16, transition:"all .2s",
            boxShadow:"0 0 30px rgba(34,197,94,.1)" }}
          onMouseEnter={e=>{e.currentTarget.style.boxShadow="0 0 40px rgba(34,197,94,.25)";e.currentTarget.style.borderColor="#4ade80";}}
          onMouseLeave={e=>{e.currentTarget.style.boxShadow="0 0 30px rgba(34,197,94,.1)";e.currentTarget.style.borderColor="#22c55e";}}>
          <div style={{ width:56, height:56, background:"#14532d", borderRadius:12,
            display:"flex", alignItems:"center", justifyContent:"center", color:"#4ade80", flexShrink:0 }}>
            <span style={{ width:28, height:28 }}>{Ic.nfc}</span>
          </div>
          <div style={{ textAlign:"left" }}>
            <p style={{ color:"#4ade80", fontSize:18, fontWeight:700, marginBottom:3 }}>NFC 스캔</p>
            <p style={{ color:"#4ade80", fontSize:13 }}>태그에 폰을 가져다 대면 장비 정보가 바로 표시됩니다</p>
          </div>
        </button>
        {nfcMsg && <div style={{ marginTop:10, background:"#1c1003", border:"1px solid #92400e",
          borderRadius:8, padding:"10px 14px", color:"#fbbf24", fontSize:13 }}>{nfcMsg}</div>}
      </div>

      {/* QR 스캔 */}
      <div style={{ padding:"0 16px 20px" }}>
        <button onClick={scanQR}
          style={{ width:"100%", background:"linear-gradient(135deg,#0c1a3b 0%,#1e3a5f 100%)",
            border:"2px solid #3b82f6", borderRadius:16, padding:"20px", cursor:"pointer",
            display:"flex", alignItems:"center", gap:16, transition:"all .2s" }}>
          <div style={{ width:56, height:56, background:"#1e40af", borderRadius:12,
            display:"flex", alignItems:"center", justifyContent:"center", color:"#60a5fa", flexShrink:0 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{width:28,height:28}}>
              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
              <rect x="3" y="14" width="7" height="7"/><path d="M14 14h3v3M17 20h3M20 17v3"/>
            </svg>
          </div>
          <div style={{ textAlign:"left" }}>
            <p style={{ color:"#60a5fa", fontSize:18, fontWeight:700, marginBottom:3 }}>QR 스캔</p>
            <p style={{ color:"#93c5fd", fontSize:13 }}>QR 코드로 장비를 바로 찾습니다</p>
          </div>
        </button>
        {qrActive && (
          <div style={{ marginTop:10, position:"relative" }}>
            <div id="qr-reader" style={{ width:"100%", borderRadius:12, overflow:"hidden" }} />
            <button onClick={stopQR}
              style={{ position:"absolute", top:8, right:8, background:"rgba(0,0,0,.7)",
                border:"1px solid #374151", borderRadius:8, padding:"6px 12px",
                color:"#e2e8f0", cursor:"pointer", fontSize:12 }}>닫기</button>
          </div>
        )}
        {qrMsg && <div style={{ marginTop:10, background:"#1c1003", border:"1px solid #92400e",
          borderRadius:8, padding:"10px 14px", color:"#fbbf24", fontSize:13 }}>{qrMsg}</div>}
      </div>

      {/* 검색 */}
      <div style={{ padding:"0 16px 16px" }}>
        <p style={{ color:"#374151", fontSize:11, letterSpacing:".1em", marginBottom:10 }}>번호 / 코드 검색</p>
        <div style={{ position:"relative" }}>
          <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", width:18, height:18, color:"#374151" }}>{Ic.find}</span>
          <input value={query} onChange={e=>setQuery(e.target.value)}
            placeholder="예: V-001, QC-002, 스프링클러..."
            style={{ width:"100%", background:"#0d150d", border:"1px solid #1a2e1a", borderRadius:10,
              padding:"12px 14px 12px 40px", color:"#e2e8f0", fontSize:14, outline:"none" }}
            onFocus={e=>e.target.style.borderColor="#4ade80"}
            onBlur={e=>e.target.style.borderColor="#1a2e1a"} />
        </div>
        {results.length > 0 && (
          <div style={{ marginTop:10, display:"flex", flexDirection:"column", gap:8 }}>
            {results.map(e => {
              const dist = userPos&&e.lat&&e.lng ? calcDist(userPos.lat,userPos.lng,Number(e.lat),Number(e.lng)) : null;
              const dir  = userPos&&e.lat&&e.lng ? calcDir(userPos.lat,userPos.lng,Number(e.lat),Number(e.lng)) : null;
              return (
                <Card key={e.id} style={{ padding:"12px 14px", cursor:"pointer" }} onClick={()=>push("detail",{eq:e})}>
                  <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                    {e.photos?.length > 0 && <img src={e.photos[0]} alt="" style={{ width:44, height:44, objectFit:"cover", borderRadius:8, flexShrink:0 }} />}
                    <div style={{ flex:1 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                        <span style={{ color:"#f0fdf4", fontSize:16, fontWeight:700 }}>{e.code}</span>
                        <TypeBadge type={e.type} small />
                        {e.nfc && <span style={{ fontSize:11, color:"#60a5fa" }}>NFC✓</span>}
                      </div>
                      <p style={{ color:"#4b5563", fontSize:12 }}>{e.hole}번홀 · {e.zone}</p>
                    </div>
                    {dist !== null && (
                      <div style={{ textAlign:"right", flexShrink:0 }}>
                        <p style={{ color:"#4ade80", fontSize:20, fontWeight:700, lineHeight:1 }}>{dir.split("")[0]}</p>
                        <p style={{ color:"#166534", fontSize:12, marginTop:2 }}>{dist}m</p>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
        {query.length > 0 && results.length === 0 && (
          <div style={{ marginTop:10, textAlign:"center", color:"#374151", fontSize:13, padding:20 }}>검색 결과가 없습니다</div>
        )}
      </div>

      {nearest && (
        <div style={{ padding:"0 16px 16px" }}>
          <p style={{ color:"#374151", fontSize:11, letterSpacing:".1em", marginBottom:10 }}>📍 가장 가까운 장비</p>
          <Card style={{ padding:16, cursor:"pointer", border:"1px solid #065f46" }} onClick={()=>push("detail",{eq:nearest.eq})}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                {nearest.eq.photos?.length > 0 && <img src={nearest.eq.photos[0]} alt="" style={{ width:52, height:52, objectFit:"cover", borderRadius:10, flexShrink:0 }} />}
                <div>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                    <span style={{ color:"#4ade80", fontSize:18, fontWeight:700 }}>{nearest.eq.code}</span>
                    <TypeBadge type={nearest.eq.type} small />
                  </div>
                  <p style={{ color:"#9ca3af", fontSize:12 }}>{nearest.eq.hole}번홀 · {nearest.eq.zone}</p>
                  {nearest.eq.notes && <p style={{ color:"#6b7280", fontSize:11, marginTop:4 }}>{nearest.eq.notes}</p>}
                </div>
              </div>
              <div style={{ textAlign:"center", flexShrink:0, marginLeft:8 }}>
                <p style={{ color:"#4ade80", fontSize:36, fontWeight:700, lineHeight:1 }}>{nearest.dir.charAt(0)}</p>
                <p style={{ color:"#166534", fontSize:11, marginTop:4 }}>{nearest.dir.slice(1)}</p>
                <p style={{ color:"#4ade80", fontSize:15, marginTop:2, fontWeight:700 }}>{nearest.dist}m</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {!userPos && (
        <div style={{ padding:"0 16px" }}>
          <Card style={{ padding:"12px 16px", border:"1px solid #92400e" }}>
            <p style={{ color:"#fbbf24", fontSize:12 }}>📡 GPS 권한을 허용하면 가까운 장비와 거리/방향이 표시됩니다</p>
          </Card>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  MAP SCREEN
// ════════════════════════════════════════════════════════════════
function MapScreen({ equipment, push, golfCourse }) {
  const [sdkReady,  setSdkReady]  = useState(false); // 카카오 SDK 로딩 완료
  const [mapReady,  setMapReady]  = useState(false); // 지도 인스턴스 생성 완료
  const [selected,  setSelected]  = useState(null);
  const [mapType,   setMapType]   = useState("normal");
  const [filterType,   setFilterType]   = useState("전체");
  const [filterCourse, setFilterCourse] = useState("전체");
  const [filterHole,   setFilterHole]   = useState("전체");
  const [showFilter,   setShowFilter]   = useState(false);
  const mapRef      = useRef(null);
  const mapObj      = useRef(null);
  const overlaysRef = useRef([]);   // 현재 마커 목록 (제거용)
  const selectedRef = useRef(null); // setSelected 클로저 문제 방지

  selectedRef.current = setSelected;

  const withGps = equipment.filter(e => e.lat && e.lng);
  const colors  = { "밸브":"#34d399","스프링클러":"#60a5fa","QC":"#fbbf24","제어밸브":"#f87171","기타":"#a78bfa" };

  const filteredEq = withGps.filter(e => {
    const matchType   = filterType === "전체" || e.type === filterType;
    const matchCourse = filterCourse === "전체" || e.course === filterCourse;
    const matchHole   = filterHole === "전체" || e.hole === filterHole;
    return matchType && matchCourse && matchHole;
  });

  const hasFilter = filterType !== "전체" || filterCourse !== "전체" || filterHole !== "전체";

  // ── Effect 1: SDK 로딩 (1회)
  useEffect(() => {
    if (window.kakao && window.kakao.maps) { setSdkReady(true); return; }
    const js = document.createElement("script");
    js.src = "//dapi.kakao.com/v2/maps/sdk.js?appkey=24351eec6a145d3f320f47e61984f69a&autoload=false&libraries=services";
    js.onload  = () => window.kakao.maps.load(() => setSdkReady(true));
    js.onerror = () => setSdkReady(true);
    document.head.appendChild(js);
  }, []);

  // ── Effect 2: 지도 인스턴스 생성 (SDK 준비 후 1회)
  useEffect(() => {
    if (!sdkReady || !mapRef.current) return;
    const kakao = window.kakao;
    if (!kakao) return;
    // 초기 중심: 골프장 GPS → 장비 GPS → 기본값
    const firstEq = equipment.find(e => e.lat && e.lng);
    let cLat = 37.2980, cLng = 127.6372, cLv = 5;
    if (golfCourse?.lat)       { cLat = Number(golfCourse.lat); cLng = Number(golfCourse.lng); cLv = 4; }
    else if (firstEq)          { cLat = Number(firstEq.lat); cLng = Number(firstEq.lng); cLv = 3; }

    setTimeout(() => {
      const map = new kakao.maps.Map(mapRef.current, {
        center: new kakao.maps.LatLng(cLat, cLng),
        level:  cLv,
      });
      mapObj.current = map;
      kakao.maps.event.addListener(map, "click", () => selectedRef.current(null));
      setMapReady(true);
    }, 100);
  }, [sdkReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Effect 3: 마커 갱신 (장비 목록 변경 시)
  useEffect(() => {
    if (!mapReady || !mapObj.current) return;
    const kakao = window.kakao;
    if (!kakao) return;
    const map = mapObj.current;

    // 기존 마커 제거
    overlaysRef.current.forEach(o => o.setMap(null));
    overlaysRef.current = [];

    // 새 마커 추가
    filteredEq.forEach(e => {
      const color   = colors[e.type] || "#a78bfa";
      const inner   = e.photos?.length > 0
        ? `<img src="${e.photos[0]}" style="width:100%;height:100%;object-fit:cover"/>`
        : "";
      const divStyle = e.photos?.length > 0
        ? `width:36px;height:36px;border-radius:50%;overflow:hidden;border:3px solid ${color};box-shadow:0 2px 6px rgba(0,0,0,.4)`
        : `width:24px;height:24px;background:${color};border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,.4)`;
      const div = document.createElement('div');
      div.style.cssText = 'cursor:pointer';
      div.innerHTML = `<div style="${divStyle}">${inner}</div>`;
      div.addEventListener('click', (ev) => {
        ev.stopPropagation();
        selectedRef.current(e);
      });
      const overlay = new kakao.maps.CustomOverlay({
        position: new kakao.maps.LatLng(Number(e.lat), Number(e.lng)),
        content: div,
        yAnchor: 1,
      });
      overlay.setMap(map);
      overlaysRef.current.push(overlay);
    });
  }, [mapReady, equipment, filterType, filterCourse, filterHole]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mapObj.current || !mapReady || !window.kakao) return;
    mapObj.current.setMapTypeId(
      mapType === "normal"
        ? window.kakao.maps.MapTypeId.ROADMAP
        : window.kakao.maps.MapTypeId.HYBRID
    );
  }, [mapType, mapReady]);  

  // ── Effect 4: 골프장 변경 시 지도 중심 이동 (항상)
  useEffect(() => {
    if (!mapReady || !mapObj.current || !window.kakao) return;
    if (!golfCourse?.lat || !golfCourse?.lng) return;
    mapObj.current.setCenter(new window.kakao.maps.LatLng(Number(golfCourse.lat), Number(golfCourse.lng)));
    mapObj.current.setLevel(4);
  }, [mapReady, golfCourse]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Effect 5: 지도 타입 전환
  useEffect(() => {
    if (!mapObj.current || !mapReady || !window.kakao) return;
    const kakao = window.kakao;
    mapObj.current.setMapTypeId(
      mapType === "normal" ? kakao.maps.MapTypeId.ROADMAP : kakao.maps.MapTypeId.HYBRID
    );
  }, [mapType, mapReady]);

  const goToMyLocation = () => {
    if (!mapObj.current) return;
    navigator.geolocation?.getCurrentPosition(
      p => {
        const kakao = window.kakao;
        const ll = new kakao.maps.LatLng(p.coords.latitude, p.coords.longitude);
        mapObj.current.setCenter(ll); mapObj.current.setLevel(3);
        new kakao.maps.CustomOverlay({
          position: ll,
          content: `<div style="width:16px;height:16px;background:#3b82f6;border:3px solid white;border-radius:50%;box-shadow:0 0 0 4px rgba(59,130,246,.3)"></div>`,
          yAnchor: 0.5,
        }).setMap(mapObj.current);
      },
      () => window.alert("GPS 권한을 허용해 주세요"),
      { enableHighAccuracy:true }
    );
  };

  return (
    <div style={{ height:"calc(100vh - 64px)", display:"flex", flexDirection:"column" }}>
      <TopBar title={golfCourse?.name || "지도"} right={
        <button onClick={() => setShowFilter(v => !v)}
          style={{ background: showFilter?"#14532d":"#0d150d",
            border:`1px solid ${showFilter?"#22c55e":"#1a2e1a"}`,
            borderRadius:8, padding:"7px 12px",
            color: showFilter?"#4ade80":"#6b7280",
            cursor:"pointer", fontSize:13, fontWeight:600,
            display:"flex", alignItems:"center", gap:4 }}>
          필터{hasFilter ? " ●" : ""}
        </button>
      } />

      {showFilter && (
        <div style={{ background:"#0a0f0a", borderBottom:"1px solid #1a2e1a",
          padding:"10px 12px", display:"flex", flexDirection:"column", gap:8 }}>
          {/* 유형 */}
          <div style={{ display:"flex", gap:6, overflowX:"auto", paddingBottom:2 }}>
            {["전체","밸브","스프링클러","QC","제어밸브","기타"].map(t => (
              <button key={t} onClick={()=>setFilterType(t)}
                style={{ flexShrink:0, padding:"5px 12px", borderRadius:20,
                  fontSize:12, cursor:"pointer", fontFamily:"inherit",
                  background: filterType===t?"#4ade80":"#0d150d",
                  color: filterType===t?"#052e16":"#9ca3af",
                  border:`1px solid ${filterType===t?"#4ade80":"#1a2e1a"}` }}>
                {t}
              </button>
            ))}
          </div>
          {/* 코스 */}
          {golfCourse?.courses?.length > 0 && (
            <div style={{ display:"flex", gap:6, overflowX:"auto", paddingBottom:2 }}>
              {["전체코스", ...golfCourse.courses.map(c=>c.name)].map(c => {
                const active = c==="전체코스" ? filterCourse==="전체" : filterCourse===c;
                return (
                  <button key={c}
                    onClick={()=>{ setFilterCourse(c==="전체코스"?"전체":c); setFilterHole("전체"); }}
                    style={{ flexShrink:0, padding:"5px 12px", borderRadius:20,
                      fontSize:12, cursor:"pointer", fontFamily:"inherit",
                      background: active?"#60a5fa":"#0d150d",
                      color: active?"#0c1a3b":"#9ca3af",
                      border:`1px solid ${active?"#60a5fa":"#1a2e1a"}` }}>
                    {c}
                  </button>
                );
              })}
            </div>
          )}
          {/* 홀 */}
          <div style={{ display:"flex", gap:6, overflowX:"auto", paddingBottom:2 }}>
            {(() => {
              let holes;
              if (filterCourse !== "전체") {
                const course = golfCourse?.courses?.find(c=>c.name===filterCourse);
                holes = course ? Array.from({length:course.holes},(_,i)=>String(i+1)) : [];
              } else {
                holes = [...new Set(
                  equipment.filter(e=>e.lat&&e.lng).map(e=>e.hole)
                )].sort((a,b)=>(parseInt(a)||99)-(parseInt(b)||99));
              }
              return ["전체홀", ...holes].map(h => {
                const active = h==="전체홀" ? filterHole==="전체" : filterHole===h;
                return (
                  <button key={h}
                    onClick={()=>setFilterHole(h==="전체홀"?"전체":h)}
                    style={{ flexShrink:0, padding:"5px 12px", borderRadius:20,
                      fontSize:12, cursor:"pointer", fontFamily:"inherit",
                      background: active?"#fbbf24":"#0d150d",
                      color: active?"#1c1003":"#9ca3af",
                      border:`1px solid ${active?"#fbbf24":"#1a2e1a"}` }}>
                    {/^\d+$/.test(h)?`${h}홀`:h}
                  </button>
                );
              });
            })()}
          </div>
          {/* 결과수 + 초기화 */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <span style={{ color:"#6b7280", fontSize:11 }}>
              {filteredEq.length}개 표시 중 / 전체 {withGps.length}개
            </span>
            <button
              onClick={()=>{setFilterType("전체");setFilterCourse("전체");setFilterHole("전체");}}
              style={{ background:"transparent", border:"none",
                color:"#6b7280", cursor:"pointer", fontSize:11 }}>
              초기화
            </button>
          </div>
        </div>
      )}

      <div style={{ flex:1, position:"relative" }}>
        {!sdkReady && (
          <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center",
            justifyContent:"center", background:"#0a0f0a", zIndex:10 }}>
            <p style={{ color:"#4ade80", fontSize:14 }}>지도 로딩중...</p>
          </div>
        )}
        <div ref={mapRef} style={{ width:"100%", height:`calc(100vh - ${showFilter ? 290 : 120}px)` }} />

        {/* 지도 컨트롤 */}
        <div style={{ position:"absolute", top:10, left:10, zIndex:1000, display:"flex", gap:4 }}>
          <button onClick={goToMyLocation}
            style={{ padding:"6px 9px", fontSize:12, borderRadius:8,
              cursor:"pointer", border:"none",
              background:"rgba(10,15,10,.9)", color:"#4ade80",
              boxShadow:"0 2px 6px rgba(0,0,0,.4)",
              display:"flex", alignItems:"center" }}>
            <span style={{width:16,height:16}}>{Ic.gps}</span>
          </button>
          {[{key:"normal",label:"일반"},{key:"skyview",label:"위성"}].map(t=>(
            <button key={t.key} onClick={()=>setMapType(t.key)}
              style={{ padding:"6px 12px", fontSize:12, borderRadius:8,
                cursor:"pointer", fontFamily:"inherit", fontWeight:600,
                border:"none",
                background:mapType===t.key?"#4ade80":"rgba(10,15,10,.9)",
                color:mapType===t.key?"#052e16":"#9ca3af",
                boxShadow:"0 2px 6px rgba(0,0,0,.4)" }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* 범례 */}
        <div style={{ position:"absolute", top:10, right:10, zIndex:1000,
          background:"rgba(10,15,10,.92)", border:"1px solid #1a2e1a",
          borderRadius:10, padding:"10px 12px", display:"flex", flexDirection:"column", gap:5 }}>
          {Object.entries(colors).map(([type, color]) => (
            <div key={type} style={{ display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ width:10, height:10, background:color, borderRadius:"50%" }} />
              <span style={{ color:"#9ca3af", fontSize:11 }}>{type}</span>
            </div>
          ))}
        </div>

        {/* 선택된 장비 카드 */}
        {selected && (
          <div style={{ position:"absolute", bottom:16, left:16, right:16, zIndex:1000 }}>
            <Card style={{ padding:"14px 16px", border:`1px solid ${colors[selected.type]||"#1a2e1a"}`,
              boxShadow:"0 4px 20px rgba(0,0,0,.6)" }}>
              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between" }}>
                <div style={{ display:"flex", gap:12, flex:1 }}>
                  {selected.photos?.length > 0 && <img src={selected.photos[0]} alt="" style={{ width:52, height:52, objectFit:"cover", borderRadius:8, flexShrink:0 }} />}
                  <div>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:5 }}>
                      <span style={{ color:colors[selected.type]||"#f0fdf4", fontSize:18, fontWeight:700,
                        fontFamily:"'DM Mono',monospace" }}>{selected.code}</span>
                      <TypeBadge type={selected.type} small />
                    </div>
                    <p style={{ color:"#4b5563", fontSize:12 }}>{holeLabel(selected)} · {selected.zone}</p>
                    {selected.notes && <p style={{ color:"#374151", fontSize:11, marginTop:4 }}>{selected.notes}</p>}
                  </div>
                </div>
                <div style={{ display:"flex", gap:6, flexShrink:0, marginLeft:10 }}>
                  <Btn onClick={()=>{push("detail",{eq:selected});setSelected(null);}}
                    style={{ padding:"6px 12px", fontSize:12 }}>상세보기</Btn>
                  <button onClick={()=>setSelected(null)}
                    style={{ background:"transparent", border:"1px solid #1a2e1a", borderRadius:8,
                      padding:"6px 10px", color:"#6b7280", cursor:"pointer", fontSize:12 }}>✕</button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {filteredEq.length === 0 && mapReady && (
          <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", zIndex:1000 }}>
            <Card style={{ padding:20, textAlign:"center" }}>
              <p style={{ color:"#374151", fontSize:13 }}>GPS 등록된 장비가 없습니다</p>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  MANAGE SCREEN
// ════════════════════════════════════════════════════════════════
function parseCsvEquipType(name) {
  if (/[Qq]/.test(name)) return "QC";
  if (name.includes('제')) return "제어밸브";
  if (name.includes('그린')) return "스프링클러";
  if (name.includes('벨')) return "밸브";
  if (name.includes('헤')) return "스프링클러";
  return null;
}

function normalizeCsvCode(name, type) {
  let n = name.replace(/그린(?!헤)/g, '그린헤드');
  n = n.replace(/헤(?!드)/g, '헤드');
  n = n.replace(/벨(?!브)/g, '밸브');
  n = n.replace(/벨브/g, '밸브');
  n = n.replace(/제(?!어)/g, '제어');
  if (type === "QC") n = n.replace(/[Qq][Cc]?(?![Cc])/g, 'QC');
  return n;
}

async function parseCsvCoords(file) {
  const buf = await file.arrayBuffer();
  const text = new TextDecoder('utf-8').decode(buf);
  const rows = text.split(/\r?\n/).filter(l => l.trim());
  if (rows.length === 0) return { results: [], skipped: 0 };

  // 헤더 자동 감지
  const firstCols = rows[0].split(',').map(c => c.trim().toLowerCase());
  const hasHeader = firstCols.includes('lat') || firstCols.includes('lon');
  const colMap = { hole: 0, name: 1, x: 2, y: 3, elevation: 4, lat: 5, lon: 6 };
  if (hasHeader) firstCols.forEach((col, i) => { colMap[col] = i; });
  const dataRows = hasHeader ? rows.slice(1) : rows;

  const results = [];
  let skipped = 0;
  for (const row of dataRows) {
    const cols = row.split(',');
    const holeVal = cols[colMap.hole]?.trim();
    const holeNum = parseInt(holeVal);
    if (!holeVal || isNaN(holeNum)) { skipped++; continue; }
    const name = cols[colMap.name]?.trim() || '';
    const elevation = cols[colMap.elevation]?.trim() || '';
    const lat = parseFloat(cols[colMap.lat]);
    const lon = parseFloat(cols[colMap.lon]);
    if (isNaN(lat) || isNaN(lon)) { skipped++; continue; }
    if (lat < 34 || lat > 38 || lon < 126 || lon > 131) { skipped++; continue; }
    const type = parseCsvEquipType(name);
    if (!type) { skipped++; continue; }
    results.push({
      id: uid(),
      code: normalizeCsvCode(name, type),
      type,
      hole: String(holeNum),
      zone: "Fairway",
      lat: Math.round(lat * 1e6) / 1e6,
      lng: Math.round(lon * 1e6) / 1e6,
      notes: `고도: ${elevation}m`,
      installed: "", photos: [], nfc: "", course: "",
    });
  }
  return { results, skipped };
}

function ManageScreen({ equipment, repairs, saveEq, saveRep, push, sortBy, onGCList }) {
  const importRef = useRef(null);
  const csvRef    = useRef(null);
  const [filter, setFilter] = useState("전체");
  const [search, setSearch] = useState("");
  const [csvPreview, setCsvPreview] = useState(null);

  const exportData = () => {
    const data = JSON.stringify({ equipment, repairs }, null, 2);
    const blob = new Blob([data], { type:"application/json" });
    const url  = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "valve-finder-backup.json"; a.click();
    URL.revokeObjectURL(url);
  };

  const shareData = () => {
    const data = JSON.stringify({ equipment, repairs }, null, 2);
    const blob = new Blob([data], { type:"application/json" });
    const file = new File([blob], "valve-finder-backup.json", { type:"application/json" });
    if (navigator.canShare && navigator.canShare({ files:[file] })) {
      navigator.share({ title:"Valve Finder 백업", files:[file] }).catch(() => {
        window.alert("💡 다운로드 받은 파일을 카톡/문자에서 파일 첨부로 공유하세요.");
      });
    } else {
      window.alert("💡 다운로드 받은 파일을 카톡/문자에서 파일 첨부로 공유하세요.");
    }
  };

  const importData = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const { equipment:eq, repairs:rep } = JSON.parse(ev.target.result);
        if (!window.confirm(`장비 ${eq.length}개, 수리 ${rep.length}건을 가져옵니다. 기존 데이터를 덮어씁니까?`)) return;
        saveEq(eq); saveRep(rep);
        window.alert("가져오기 완료!");
      } catch { window.alert("파일 형식이 올바르지 않습니다"); }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const importCsv = async e => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    try {
      const { results, skipped } = await parseCsvCoords(file);
      const counts = { 밸브:0, 스프링클러:0, QC:0, 제어밸브:0 };
      results.forEach(r => { if (counts[r.type] !== undefined) counts[r.type]++; });
      setCsvPreview({ results, skipped, counts });
    } catch (err) {
      window.alert("CSV 파싱 오류: " + err.message);
    }
  };

  const confirmCsvImport = () => {
    if (!csvPreview) return;
    const merged = [...equipment];
    csvPreview.results.forEach(item => {
      const idx = merged.findIndex(x => x.code === item.code);
      if (idx >= 0) merged[idx] = item;
      else merged.push(item);
    });
    saveEq(merged);
    setCsvPreview(null);
    window.alert(`${csvPreview.results.length}개 등록 완료!`);
  };

  const filters = ["전체", ...EQUIP_TYPES];
  const list = equipment.filter(e => {
    const matchType = filter==="전체" || e.type===filter;
    const q = search.toLowerCase();
    const matchQ = !q || e.code.toLowerCase().includes(q) || e.notes?.toLowerCase().includes(q);
    if (sortBy==="repair")    { return matchType && matchQ && repairs.some(r => r.eqId===e.id); }
    if (sortBy==="thismonth") {
      const thisMonth = new Date().toISOString().slice(0,7);
      return matchType && matchQ && repairs.some(r => r.eqId===e.id && r.date?.slice(0,7)===thisMonth);
    }
    return matchType && matchQ;
  }).sort((a, b) => {
    if (sortBy==="repair" || sortBy==="thismonth") {
      const lastA = repairs.filter(r=>r.eqId===a.id).sort((x,y)=>y.date.localeCompare(x.date))[0]?.date || "";
      const lastB = repairs.filter(r=>r.eqId===b.id).sort((x,y)=>y.date.localeCompare(x.date))[0]?.date || "";
      return lastB.localeCompare(lastA);
    }
    const ha = parseInt(a.hole) || 999;
    const hb = parseInt(b.hole) || 999;
    if (ha !== hb) return ha - hb;
    return (a.code || '').localeCompare(b.code || '');
  });

  return (
    <div className="slide-up" style={{ paddingBottom:80, minHeight:"100vh" }}>
      <TopBar title="관리" right={
        <Btn onClick={()=>push("eqForm",{eq:null})} style={{ padding:"7px 12px", fontSize:13, gap:4 }}>
          <span style={{ width:16, height:16 }}>{Ic.plus}</span> 등록
        </Btn>
      } />

      {/* 골프장 설정 버튼 */}
      <div style={{ padding:"8px 16px 0" }}>
        <button onClick={onGCList}
          style={{ width:"100%", display:"flex", alignItems:"center", gap:10, padding:"12px 14px",
            background:"#0d150d", border:"1px solid #1a2e1a", borderRadius:10, cursor:"pointer",
            transition:"border-color .15s" }}
          onMouseEnter={e=>e.currentTarget.style.borderColor="#4ade80"}
          onMouseLeave={e=>e.currentTarget.style.borderColor="#1a2e1a"}>
          <span style={{ width:18, height:18, color:"#4ade80" }}>{Ic.golf}</span>
          <span style={{ color:"#9ca3af", fontSize:13 }}>골프장 / 지도 중심 설정</span>
          <span style={{ marginLeft:"auto", color:"#374151", fontSize:12 }}>›</span>
        </button>
      </div>

      <div style={{ padding:"8px 16px", display:"flex", flexDirection:"column", gap:6 }}>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={exportData}
            style={{ flex:1, padding:10, background:"#052e16", border:"1px solid #166534",
              borderRadius:8, color:"#4ade80", cursor:"pointer", fontSize:12, fontWeight:600 }}>💾 다운로드</button>
          <button onClick={shareData}
            style={{ flex:1, padding:10, background:"#0c1a3b", border:"1px solid #1e40af",
              borderRadius:8, color:"#60a5fa", cursor:"pointer", fontSize:12, fontWeight:600 }}>📤 공유</button>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={()=>importRef.current?.click()}
            style={{ flex:1, padding:10, background:"#1c1003", border:"1px solid #92400e",
              borderRadius:8, color:"#fbbf24", cursor:"pointer", fontSize:12, fontWeight:600 }}>📥 JSON 가져오기</button>
          <button onClick={()=>csvRef.current?.click()}
            style={{ flex:1, padding:10, background:"#1a0a2e", border:"1px solid #5b21b6",
              borderRadius:8, color:"#a78bfa", cursor:"pointer", fontSize:12, fontWeight:600 }}>📍 좌표 가져오기</button>
        </div>
        <input ref={importRef} type="file" accept=".json" style={{ display:"none" }} onChange={importData} />
        <input ref={csvRef}    type="file" accept=".csv"  style={{ display:"none" }} onChange={importCsv} />
      </div>

      {csvPreview && (
        <div style={{ margin:"0 16px 8px", padding:14, background:"#0d0d1a", border:"1px solid #5b21b6",
          borderRadius:10, fontSize:13, color:"#c4b5fd" }}>
          <p style={{ fontWeight:700, marginBottom:8 }}>📍 좌표 미리보기</p>
          <p>{csvPreview.results.length}개 등록 예정 — 밸브 {csvPreview.counts["밸브"]} | 스프링클러 {csvPreview.counts["스프링클러"]} | QC {csvPreview.counts["QC"]} | 제어밸브 {csvPreview.counts["제어밸브"]}</p>
          <p style={{ color:"#6b7280", fontSize:11, marginTop:4 }}>스킵 {csvPreview.skipped}개</p>
          <div style={{ display:"flex", gap:8, marginTop:12 }}>
            <button onClick={()=>setCsvPreview(null)}
              style={{ flex:1, padding:"8px 0", background:"transparent", border:"1px solid #374151",
                borderRadius:8, color:"#9ca3af", cursor:"pointer", fontSize:13 }}>취소</button>
            <button onClick={confirmCsvImport}
              style={{ flex:1, padding:"8px 0", background:"#2e1065", border:"1px solid #5b21b6",
                borderRadius:8, color:"#a78bfa", cursor:"pointer", fontSize:13, fontWeight:700 }}>
              {csvPreview.results.length}개 등록</button>
          </div>
        </div>
      )}

      <div style={{ padding:"0 16px", display:"flex", flexDirection:"column", gap:10 }}>
        <div style={{ position:"relative" }}>
          <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", width:16, height:16, color:"#374151" }}>{Ic.find}</span>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="코드, 메모 검색..."
            style={{ width:"100%", background:"#0d150d", border:"1px solid #1a2e1a", borderRadius:9,
              padding:"10px 14px 10px 38px", color:"#e2e8f0", fontSize:14, outline:"none" }}
            onFocus={e=>e.target.style.borderColor="#4ade80"}
            onBlur={e=>e.target.style.borderColor="#1a2e1a"} />
        </div>
        <div style={{ display:"flex", gap:6, overflowX:"auto", paddingBottom:4 }}>
          {filters.map(f => {
            const s = TYPE_STYLE[f];
            const active = filter===f;
            return (
              <button key={f} onClick={()=>setFilter(f)}
                style={{ flexShrink:0, padding:"6px 14px", borderRadius:20, fontSize:12, cursor:"pointer",
                  fontFamily:"inherit", fontWeight:active?700:400, transition:"all .15s",
                  background:active?(s?.bg||"#052e16"):"#0d150d",
                  border:`1px solid ${active?(s?.border||"#166534"):"#1a2e1a"}`,
                  color:active?(s?.color||"#4ade80"):"#4b5563" }}>{f}</button>
            );
          })}
        </div>
        <p style={{ color:"#374151", fontSize:11 }}>{list.length}개 장비</p>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {list.map(e => {
            const repCount = repairs.filter(r => r.eqId===e.id).length;
            const ts = TYPE_STYLE[e.type] || TYPE_STYLE["기타"];
            return (
              <Card key={e.id} style={{ padding:"14px 16px" }}>
                <div style={{ display:"flex", alignItems:"flex-start" }}>
                  {e.photos?.length > 0 && (
                    <img src={e.photos[0]} alt="" style={{ width:52, height:52, objectFit:"cover",
                      borderRadius:10, marginRight:12, flexShrink:0, border:"1px solid #1a2e1a" }} />
                  )}
                  <div style={{ flex:1, cursor:"pointer" }} onClick={()=>push("detail",{eq:e})}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6, flexWrap:"wrap" }}>
                      <span style={{ color:ts.color, fontSize:16, fontWeight:700, fontFamily:"'DM Mono',monospace" }}>{e.code}</span>
                      <TypeBadge type={e.type} small />
                      {e.nfc && <span style={{ fontSize:10, color:"#60a5fa", background:"#0c1a3b",
                        border:"1px solid #1e40af", borderRadius:20, padding:"2px 7px" }}>NFC</span>}
                      {!e.lat && <span style={{ fontSize:10, color:"#4b5563", background:"#111",
                        border:"1px solid #1a2e1a", borderRadius:20, padding:"2px 7px" }}>GPS미등록</span>}
                      {e.photos?.length > 0 && <span style={{ fontSize:10, color:"#4ade80", background:"#022c22",
                        border:"1px solid #065f46", borderRadius:20, padding:"2px 7px" }}>📷 {e.photos.length}</span>}
                    </div>
                    <p style={{ color:"#9ca3af", fontSize:12 }}>
                      {e.hole}번홀 · {e.zone}{repCount>0 && ` · 수리 ${repCount}회`}
                    </p>
                    {e.notes && <p style={{ color:"#6b7280", fontSize:11, marginTop:4 }}>{e.notes}</p>}
                  </div>
                  <button onClick={ev=>{ev.stopPropagation();if(window.confirm("삭제하시겠습니까?")){saveEq(equipment.filter(x=>x.id!==e.id));saveRep(repairs.filter(r=>r.eqId!==e.id));}}}
                    style={{ background:"transparent", border:"1px solid #450a0a", borderRadius:6,
                      padding:6, color:"#f87171", cursor:"pointer", width:30, height:30,
                      display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginLeft:8, alignSelf:"center" }}>
                    {Ic.trash}
                  </button>
                </div>
              </Card>
            );
          })}
          {list.length === 0 && (
            <div style={{ textAlign:"center", color:"#374151", padding:40, fontSize:13 }}>
              {search ? "검색 결과가 없습니다" : "등록된 장비가 없습니다"}
            </div>
          )}
        </div>

        {equipment.length > 0 && (
          <div style={{ marginTop:16 }}>
            <button
              onClick={() => {
                if (window.confirm(`장비 ${equipment.length}개와 수리기록 ${repairs.length}건을 모두 삭제하시겠습니까?`)) {
                  saveEq([]);
                  saveRep([]);
                }
              }}
              style={{ width:"100%", padding:12, background:"#1a0000", border:"1px solid #7f1d1d",
                borderRadius:8, color:"#f87171", cursor:"pointer", fontSize:13, fontWeight:600 }}>
              🗑️ 장비 전체 삭제
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  DETAIL SCREEN
// ════════════════════════════════════════════════════════════════
function DetailScreen({ equipment, repairs, saveRep, push, pop, goHome, eq:initEq }) {
  const eq     = equipment.find(e => e.id===initEq?.id) || initEq;
  const eqReps = repairs.filter(r => r.eqId===eq?.id).sort((a,b)=>b.date.localeCompare(a.date));

  useEffect(() => {
    if (!eq) return;
    const gen = () => {
      try {
        const QRCode = window.QRCode;
        if (!QRCode) return setTimeout(gen, 300);
        const container = document.getElementById(`qr-${eq.id}`);
        if (!container) return;
        container.innerHTML = "";
        new QRCode(container, { text:eq.code, width:180, height:180, colorDark:"#052e16", colorLight:"#ffffff" });
      } catch(err) { /* QR 생성 실패 무시 */ }
    };
    if (!window.QRCode) {
      const js = document.createElement("script");
      js.src = "https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js";
      js.onload = () => setTimeout(gen, 100);
      document.head.appendChild(js);
    } else setTimeout(gen, 100);
  }, [eq?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!eq) return null;
  const ts = TYPE_STYLE[eq.type] || TYPE_STYLE["기타"];
  const delRep = id => { if(window.confirm("수리 내역을 삭제하시겠습니까?")) saveRep(repairs.filter(r=>r.id!==id)); };

  return (
    <div className="slide-up" style={{ paddingBottom:80, minHeight:"100vh" }}>
      <TopBar title={eq.code} onBack={pop} onHome={goHome} right={
        <Btn variant="ghost" onClick={()=>push("eqForm",{eq})} style={{ padding:"7px 12px", fontSize:13, gap:4 }}>
          <span style={{ width:14, height:14 }}>{Ic.edit}</span> 수정
        </Btn>
      } />

      <div style={{ padding:16, display:"flex", flexDirection:"column", gap:14 }}>
        {eq.photos?.length > 0 && (
          <div>
            <p style={{ color:"#374151", fontSize:11, letterSpacing:".1em", marginBottom:8 }}>장비 사진</p>
            <PhotoStrip photos={eq.photos} />
          </div>
        )}

        {/* 장비 정보 */}
        <Card style={{ padding:16 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
            <div style={{ width:48, height:48, background:ts.bg, border:`1px solid ${ts.border}`,
              borderRadius:12, display:"flex", alignItems:"center", justifyContent:"center",
              color:ts.color, fontSize:22, flexShrink:0 }}>
              {{"밸브":"🔵","스프링클러":"💧","QC":"🔶","제어밸브":"🔴","기타":"⚙️"}[eq.type]}
            </div>
            <div>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                <h2 style={{ color:"#f0fdf4", fontSize:22, fontWeight:700, fontFamily:"'DM Mono',monospace" }}>{eq.code}</h2>
                <TypeBadge type={eq.type} />
              </div>
              <p style={{ color:"#4b5563", fontSize:12 }}>설치일: {eq.installed||"미등록"}</p>
            </div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            {[
              { label:"홀",  value:`${eq.hole}번홀` },
              { label:"구역",value:eq.zone },
              { label:"GPS", value:eq.lat?`${Number(eq.lat).toFixed(5)}, ${Number(eq.lng).toFixed(5)}`:"미등록" },
              { label:"NFC", value:eq.nfc||"미등록" },
            ].map(item => (
              <div key={item.label} style={{ background:"#0a0f0a", borderRadius:8, padding:"10px 12px" }}>
                <p style={{ color:"#6b7280", fontSize:10, marginBottom:3 }}>{item.label}</p>
                <p style={{ color:"#f0fdf4", fontSize:13 }}>{item.value}</p>
              </div>
            ))}
          </div>
          {eq.notes && (
            <div style={{ marginTop:10, background:"#0a0f0a", borderRadius:8, padding:"10px 12px" }}>
              <p style={{ color:"#6b7280", fontSize:10, marginBottom:3 }}>메모</p>
              <p style={{ color:"#f0fdf4", fontSize:13 }}>{eq.notes}</p>
            </div>
          )}
        </Card>

        {/* QR 코드 */}
        <div>
          <p style={{ color:"#374151", fontSize:11, letterSpacing:".1em", marginBottom:8 }}>QR 코드</p>
          <Card style={{ padding:16, display:"flex", flexDirection:"column", alignItems:"center", gap:10 }}>
            <div id={`qr-${eq.id}`} style={{ borderRadius:8 }} />
            <p style={{ color:"#4b5563", fontSize:11 }}>{eq.code} — 카메라로 스캔하세요</p>
            <button onClick={() => {
              const container = document.getElementById(`qr-${eq.id}`);
              const tryDl = (retry=0) => {
                const img    = container?.querySelector("img");
                const canvas = container?.querySelector("canvas");
                let url;
                if (canvas) url = canvas.toDataURL("image/png");
                else if (img && img.src && !img.src.startsWith("data:image/gif")) url = img.src;
                if (!url && retry < 5) return setTimeout(()=>tryDl(retry+1), 300);
                if (!url) return window.alert("QR 코드를 먼저 로딩해주세요");
                const a = document.createElement("a");
                a.href = url; a.download = `QR-${eq.code}.png`; a.click();
              };
              tryDl();
            }} style={{ padding:"8px 20px", background:"#052e16", border:"1px solid #166534",
              borderRadius:8, color:"#4ade80", cursor:"pointer", fontSize:13, fontWeight:600 }}>
              💾 QR 이미지 저장
            </button>
          </Card>
        </div>

        {/* 수리 내역 */}
        <div>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
            <p style={{ color:"#374151", fontSize:11, letterSpacing:".1em" }}>수리 내역 ({eqReps.length})</p>
            <Btn onClick={()=>push("repairForm",{eqId:eq.id,repair:null})}
              style={{ padding:"6px 12px", fontSize:12, gap:4 }}>
              <span style={{ width:14, height:14 }}>{Ic.plus}</span> 기록
            </Btn>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {eqReps.map(r => (
              <Card key={r.id} style={{ padding:"12px 14px" }}>
                <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between" }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:5 }}>
                      <span style={{ background:"#1c1003", border:"1px solid #92400e", color:"#fbbf24",
                        fontSize:11, padding:"2px 9px", borderRadius:20 }}>{r.type}</span>
                      <span style={{ color:"#4b5563", fontSize:11 }}>{r.date} · {r.worker}</span>
                    </div>
                    {r.parts && <p style={{ color:"#9ca3af", fontSize:12, marginBottom:3 }}>부품: {r.parts}</p>}
                    {r.notes && <p style={{ color:"#9ca3af", fontSize:12, marginBottom:r.photos?.length?8:0 }}>{r.notes}</p>}
                    {r.photos?.length > 0 && <PhotoStrip photos={r.photos} />}
                  </div>
                  <div style={{ display:"flex", gap:4, marginLeft:8 }}>
                    <button onClick={()=>push("repairForm",{eqId:eq.id,repair:r})}
                      style={{ background:"transparent", border:"none", color:"#374151", cursor:"pointer", padding:4 }}>{Ic.edit}</button>
                    <button onClick={()=>delRep(r.id)}
                      style={{ background:"transparent", border:"none", color:"#7f1d1d", cursor:"pointer", padding:4 }}>{Ic.trash}</button>
                  </div>
                </div>
              </Card>
            ))}
            {eqReps.length === 0 && (
              <Card style={{ padding:20, textAlign:"center" }}>
                <p style={{ color:"#374151", fontSize:13 }}>수리 내역이 없습니다</p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  EQUIPMENT FORM
// ════════════════════════════════════════════════════════════════
function EqFormScreen({ equipment, saveEq, push, pop, goHome, eq:initEq, golfCourse }) {
  const isEdit  = !!initEq;
  const blank   = { id:uid(), type:"밸브", code:"", hole:"1", course:"", zone:"Fairway", lat:"", lng:"", nfc:"", notes:"", installed:new Date().toISOString().slice(0,10), photos:[] };
  const [f, setF] = useState(isEdit ? { course:"", ...initEq, photos:initEq.photos||[] } : blank);
  const set = (k, v) => setF(p => ({ ...p, [k]:v }));

  const courses   = golfCourse?.courses || [];
  const hasCourse = courses.length > 0;
  // 선택된 코스의 홀 수 (기본 18)
  const selCourse = courses.find(c => c.name === f.course);
  const holeCount = selCourse ? selCourse.holes : 18;
  const holeOpts  = hasCourse
    ? [...Array(holeCount)].map((_,i)=>String(i+1)).concat(["연습장","클럽하우스"])
    : HOLES;

  const save = () => {
    if (!f.code.trim()) return window.alert("코드를 입력하세요");
    const parsed = { ...f, lat:f.lat?parseFloat(f.lat):null, lng:f.lng?parseFloat(f.lng):null };
    if (isEdit) saveEq(equipment.map(e => e.id===f.id ? parsed : e));
    else        saveEq([...equipment, parsed]);
    pop();
  };

  return (
    <div className="slide-up" style={{ paddingBottom:80, minHeight:"100vh" }}>
      <TopBar title={isEdit?"장비 수정":"장비 등록"} onBack={pop} onHome={goHome} />
      <div style={{ padding:16, display:"flex", flexDirection:"column", gap:14 }}>

        <div>
          <label style={{ display:"block", color:"#6b7280", fontSize:12, marginBottom:8, letterSpacing:".04em" }}>장비 유형</label>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {EQUIP_TYPES.map(t => {
              const s = TYPE_STYLE[t];
              const active = f.type===t;
              return (
                <button key={t} onClick={()=>set("type",t)}
                  style={{ padding:"8px 16px", borderRadius:8, cursor:"pointer", fontFamily:"inherit",
                    fontSize:13, fontWeight:active?700:400, transition:"all .15s",
                    background:active?s.bg:"#0d150d",
                    border:`1px solid ${active?s.border:"#1a2e1a"}`,
                    color:active?s.color:"#4b5563" }}>{t}</button>
              );
            })}
          </div>
        </div>

        <Inp label="코드 *" placeholder="예: V-023, QC-001" value={f.code} onChange={e=>set("code",e.target.value)} />

        {/* 코스 선택 (골프장에 코스가 등록된 경우에만 표시) */}
        {hasCourse && (
          <Sel label="코스" value={f.course} onChange={e=>{ set("course",e.target.value); set("hole","1"); }}>
            <option value="">코스 선택 안함</option>
            {courses.map(c=><option key={c.id} value={c.name}>{c.name} ({c.holes}홀)</option>)}
          </Sel>
        )}

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <Sel label="홀 번호" value={f.hole} onChange={e=>set("hole",e.target.value)}>
            {holeOpts.map(h=><option key={h} value={h}>{/^\d+$/.test(h)?`${h}번홀`:h}</option>)}
          </Sel>
          <Sel label="구역 (Zone)" value={f.zone} onChange={e=>set("zone",e.target.value)}>
            {ZONES.map(z=><option key={z} value={z}>{z}</option>)}
          </Sel>
        </div>

        <Btn variant="green" style={{ width:"100%", marginBottom:8 }}
          onClick={() => {
            navigator.geolocation?.getCurrentPosition(
              p => {
                setF(prev => ({
                  ...prev,
                  lat: p.coords.latitude.toFixed(6),
                  lng: p.coords.longitude.toFixed(6)
                }));
                window.alert("현재 위치로 GPS 갱신됐습니다");
              },
              () => window.alert("GPS를 사용할 수 없습니다"),
              { enableHighAccuracy: true }
            );
          }}>
          📍 현재 위치로 GPS 갱신
        </Btn>
        <GpsField lat={f.lat} lng={f.lng} onChange={(lat,lng)=>setF(p=>({...p,lat,lng}))} />

        <Card style={{ padding:14 }}>
          <PhotoCapture photos={f.photos||[]} onChange={photos=>set("photos",photos)} label="장비 사진" max={5} />
        </Card>

        <Inp label="NFC 태그 ID" placeholder="예: NFC-023" value={f.nfc||""} onChange={e=>set("nfc",e.target.value)} />
        <Inp label="설치일" type="date" value={f.installed||""} onChange={e=>set("installed",e.target.value)} />

        <div>
          <label style={{ display:"block", color:"#6b7280", fontSize:12, marginBottom:6, letterSpacing:".04em" }}>메모</label>
          <textarea value={f.notes||""} onChange={e=>set("notes",e.target.value)} rows={3}
            placeholder="모델명, 위치 설명, 특이사항..."
            style={{ width:"100%", background:"#0d150d", border:"1px solid #1a2e1a", borderRadius:8,
              padding:"10px 14px", color:"#e2e8f0", fontSize:14, resize:"none" }}
            onFocus={e=>e.target.style.borderColor="#4ade80"}
            onBlur={e=>e.target.style.borderColor="#1a2e1a"} />
        </div>

        <div style={{ display:"flex", gap:10, marginTop:4 }}>
          <Btn variant="ghost" style={{ flex:1 }} onClick={pop}>취소</Btn>
          <Btn style={{ flex:2 }} onClick={save}>{isEdit?"수정 완료":"등록 완료"}</Btn>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  REPAIR FORM
// ════════════════════════════════════════════════════════════════
function RepairFormScreen({ equipment, repairs, saveRep, pop, goHome, eqId, repair:initRep }) {
  const isEdit = !!initRep;
  const blank  = { id:uid(), eqId, date:new Date().toISOString().slice(0,10), worker:"", type:"점검", parts:"", notes:"", photos:[] };
  const [f, setF] = useState(isEdit ? { ...initRep, photos:initRep.photos||[] } : blank);
  const set = (k, v) => setF(p => ({ ...p, [k]:v }));

  const save = () => {
    if (!f.worker.trim()) return window.alert("작업자를 입력하세요");
    if (isEdit) saveRep(repairs.map(r => r.id===f.id ? f : r));
    else        saveRep([...repairs, f]);
    pop();
  };

  const eq = equipment.find(e => e.id===(f.eqId||eqId));

  return (
    <div className="slide-up" style={{ paddingBottom:80, minHeight:"100vh" }}>
      <TopBar title={isEdit?"수리 수정":"수리 기록"} onBack={pop} onHome={goHome} />
      <div style={{ padding:16, display:"flex", flexDirection:"column", gap:14 }}>

        {eq && (
          <Card style={{ padding:"12px 16px", display:"flex", alignItems:"center", gap:10, border:"1px solid #065f46" }}>
            {eq.photos?.length > 0 && (
              <img src={eq.photos[0]} alt="" style={{ width:36, height:36, objectFit:"cover", borderRadius:8, flexShrink:0 }} />
            )}
            <TypeBadge type={eq.type} />
            <span style={{ color:"#4ade80", fontWeight:700, fontFamily:"'DM Mono',monospace" }}>{eq.code}</span>
            <span style={{ color:"#4b5563", fontSize:12 }}>{eq.hole}번홀 · {eq.zone}</span>
          </Card>
        )}

        {!eq && (
          <Sel label="대상 장비" value={f.eqId} onChange={e=>set("eqId",e.target.value)}>
            <option value="">선택하세요</option>
            {equipment.map(e=><option key={e.id} value={e.id}>{e.code} ({e.type})</option>)}
          </Sel>
        )}

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <Inp label="수리일" type="date" value={f.date} onChange={e=>set("date",e.target.value)} />
          <Inp label="작업자 *" placeholder="이름" value={f.worker} onChange={e=>set("worker",e.target.value)} />
        </div>

        <div>
          <label style={{ display:"block", color:"#6b7280", fontSize:12, marginBottom:8, letterSpacing:".04em" }}>작업 유형</label>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {WORK_TYPES.map(t => (
              <button key={t} onClick={()=>set("type",t)}
                style={{ padding:"7px 14px", borderRadius:8, cursor:"pointer", fontFamily:"inherit", fontSize:12,
                  transition:"all .15s",
                  background:f.type===t?"#1c1003":"#0d150d",
                  border:`1px solid ${f.type===t?"#92400e":"#1a2e1a"}`,
                  color:f.type===t?"#fbbf24":"#4b5563" }}>{t}</button>
            ))}
          </div>
        </div>

        <Inp label="사용 부품" placeholder="예: 노즐 #3, 필터, 실링" value={f.parts} onChange={e=>set("parts",e.target.value)} />

        <div>
          <label style={{ display:"block", color:"#6b7280", fontSize:12, marginBottom:6, letterSpacing:".04em" }}>작업 내용</label>
          <textarea value={f.notes} onChange={e=>set("notes",e.target.value)} rows={3}
            placeholder="수리 내용, 상태, 특이사항..."
            style={{ width:"100%", background:"#0d150d", border:"1px solid #1a2e1a", borderRadius:8,
              padding:"10px 14px", color:"#e2e8f0", fontSize:14, resize:"none" }}
            onFocus={e=>e.target.style.borderColor="#4ade80"}
            onBlur={e=>e.target.style.borderColor="#1a2e1a"} />
        </div>

        <Card style={{ padding:14 }}>
          <PhotoCapture photos={f.photos||[]} onChange={photos=>set("photos",photos)} label="수리 사진 (전/후)" max={4} />
        </Card>

        <div style={{ display:"flex", gap:10 }}>
          <Btn variant="ghost" style={{ flex:1 }} onClick={pop}>취소</Btn>
          <Btn style={{ flex:2 }} onClick={save}>{isEdit?"수정 완료":"기록 완료"}</Btn>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  🌱 SOIL SCREEN (POGO BLE)
// ════════════════════════════════════════════════════════════════
function SoilScreen({ soilData, saveSoilData, activeGC, soilThreshold, saveSoilThreshold }) {
  const [innerTab,      setInnerTab]      = useState("list");
  const [showThreshold,  setShowThreshold]  = useState(false);
  const [localThreshold, setLocalThreshold] = useState({
    dry: String(soilThreshold.dry),
    caution: String(soilThreshold.caution),
    wet: String(soilThreshold.wet),
  });
  useEffect(() => {
    if (showThreshold) {
      setLocalThreshold({
        dry: String(soilThreshold.dry),
        caution: String(soilThreshold.caution),
        wet: String(soilThreshold.wet),
      });
    }
  }, [showThreshold]); // eslint-disable-line react-hooks/exhaustive-deps
  const [bleStatus,  setBleStatus]  = useState("idle"); // idle|connecting|connected|error
  const [battery,    setBattery]    = useState(null);
  const [measuring,   setMeasuring]  = useState(false);
  const [lastPacket,  setLastPacket] = useState("");
  const [pending,    setPending]    = useState(null);
  const [saveHole,   setSaveHole]   = useState("1");
  const [saveCourse, setSaveCourse] = useState("");
  const [saveMemo,   setSaveMemo]   = useState("");
  const rxCharRef           = useRef(null);
  const deviceRef           = useRef(null);
  const notifyCountRef      = useRef(0);
  const isMeasuringRef      = useRef(false);
  const measureBufferRef    = useRef({});
  const bleRawBufferRef     = useRef("");
  const measureTimeRef      = useRef("");

  const courses   = activeGC?.courses || [];
  const hasCourse = courses.length > 0;
  const selCourse = courses.find(c => c.name === saveCourse);
  const holeCount = selCourse ? selCourse.holes : 18;
  const holeOpts  = hasCourse
    ? [...Array(holeCount)].map((_,i)=>String(i+1)).concat(["연습장","클럽하우스"])
    : HOLES;

  useEffect(() => {
    return () => {
      if (deviceRef.current?.gatt?.connected) deviceRef.current.gatt.disconnect();
    };
  }, []);

  const connectBLE = async () => {
    if (!navigator.bluetooth) {
      window.alert("이 브라우저는 Web Bluetooth를 지원하지 않습니다.\nChrome(Android/PC)에서 사용해 주세요.");
      return;
    }
    setBleStatus("connecting");
    try {
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ name: "PM263158" }],
        optionalServices: [POGO_SERVICE],
      });
      deviceRef.current = device;
      device.addEventListener("gattserverdisconnected", () => {
        setBleStatus("idle"); setBattery(null); rxCharRef.current = null;
      });
      const server  = await device.gatt.connect();
      const service = await server.getPrimaryService(POGO_SERVICE);
      const rx      = await service.getCharacteristic(POGO_RX);
      const tx      = await service.getCharacteristic(POGO_TX);
      rxCharRef.current = rx;
      const handleNotification = (ev) => {
        notifyCountRef.current++;
        const chunk = new TextDecoder().decode(ev.target.value);

        // raw 원본 UI 표시
        setLastPacket(chunk);

        // 끊긴 패킷 이어붙이기
        bleRawBufferRef.current += chunk;

        // 완전한 JSON 추출 (중괄호 매칭)
        let buf = bleRawBufferRef.current;
        let start = buf.indexOf("{");
        while (start !== -1) {
          let depth = 0;
          let end = -1;
          for (let i = start; i < buf.length; i++) {
            if (buf[i] === "{") depth++;
            if (buf[i] === "}") depth--;
            if (depth === 0) { end = i; break; }
          }
          if (end === -1) break; // 아직 끊긴 상태

          const jsonStr = buf.slice(start, end + 1);
          buf = buf.slice(end + 1).trimStart();
          bleRawBufferRef.current = buf;
          start = buf.indexOf("{");

          try {
            const json = JSON.parse(jsonStr);

            // 배터리
            if (json.pogo_vbatt !== undefined) {
              const mv  = parseInt(json.pogo_vbatt);
              const pct = Math.round(Math.min(100, Math.max(0,
                (mv - 3200) / (4200 - 3200) * 100)));
              setBattery(pct);
              continue;
            }

            // 측정값 수집
            if (json.hp_moisture        !== undefined)
              measureBufferRef.current.moisture  = parseFloat(json.hp_moisture) * 100;
            if (json.hp_ec              !== undefined)
              measureBufferRef.current.ec        = parseFloat(json.hp_ec) * 10;
            if (json.hp_ec_tc           !== undefined)
              measureBufferRef.current.ec_tc     = parseFloat(json.hp_ec_tc) * 10;
            if (json.hp_tempc           !== undefined)
              measureBufferRef.current.tempc     = parseFloat(json.hp_tempc);
            if (json.hp_tempf           !== undefined)
              measureBufferRef.current.tempf     = parseFloat(json.hp_tempf);
            if (json.hp_imaginary       !== undefined)
              measureBufferRef.current.imaginary = parseFloat(json.hp_imaginary);
          } catch (e) {
            setLastPacket("❌ parse fail: " + jsonStr);
          }
        }

        // 버퍼 오염 방지 (1000자 초과 시 초기화)
        if (bleRawBufferRef.current.length > 1000) {
          bleRawBufferRef.current = "";
        }
      };
      await tx.startNotifications();
      tx.addEventListener("characteristicvaluechanged", handleNotification);
      tx.oncharacteristicvaluechanged = handleNotification;

      setBleStatus("connected");
    } catch (err) {
      setBleStatus(err.name === "NotFoundError" ? "idle" : "error");
    }
  };

  const disconnectBLE = () => {
    if (deviceRef.current?.gatt?.connected) deviceRef.current.gatt.disconnect();
    setBleStatus("idle"); setBattery(null); rxCharRef.current = null;
  };

  const measure = async () => {
    if (!rxCharRef.current) return;

    // 버퍼 초기화 + 측정 플래그
    const now = new Date();
    const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
    measureTimeRef.current = kst.toISOString().slice(0,16).replace("T"," ");
    measureBufferRef.current = {};
    bleRawBufferRef.current  = "";
    isMeasuringRef.current   = true;
    setMeasuring(true);

    try {
      await rxCharRef.current.writeValueWithoutResponse(
        new TextEncoder().encode("sample")
      );
    } catch {
      // write 실패해도 계속 진행 (스트리밍 중이라 데이터 올 수 있음)
    }

    // sample 명령 후 5초 뒤 버퍼에 있는 값으로 완료 처리
    setTimeout(() => {
      if (!isMeasuringRef.current) return;
      isMeasuringRef.current = false;
      setMeasuring(false);

      const collected = measureBufferRef.current;
      if (collected.moisture === undefined && collected.ec === undefined) {
        window.alert("측정값을 받지 못했습니다. 다시 시도하세요.");
        return;
      }

      // 표시값(반올림) 기준으로 계산
      const moisture = parseFloat((collected.moisture ?? 0).toFixed(1));
      const ec       = parseFloat((collected.ec ?? 0).toFixed(2));
      const tempc    = collected.tempc ? parseFloat(collected.tempc.toFixed(1)) : null;

      // 염도 계산 (배율 적용된 값 기준)
      const salinity = moisture > 0
        ? parseFloat((ec / moisture * 100).toFixed(2))
        : 0;

      console.log("염도계산:", ec, "/", moisture, "×100 =", salinity);

      navigator.geolocation?.getCurrentPosition(
        p  => setPending({ moisture, ec, tempc, salinity, date: measureTimeRef.current,
                           lat: p.coords.latitude, lng: p.coords.longitude }),
        () => setPending({ moisture, ec, tempc, salinity, date: measureTimeRef.current,
                           lat: null, lng: null }),
        { enableHighAccuracy: true, timeout: 5000 }
      );
    }, 5000);
  };

  const saveMeasurement = () => {
    if (!pending) return;
    const entry = {
      id:       uid(),
      date:     pending.date ?? new Date().toISOString().slice(0,16).replace("T"," "),
      hole:     saveHole,
      course:   saveCourse,
      lat:      pending.lat,
      lng:      pending.lng,
      moisture: pending.moisture != null ? parseFloat(pending.moisture.toFixed(1)) : null,
      ec:       pending.ec      != null ? parseFloat(pending.ec.toFixed(2))       : pending.ec_tc != null ? parseFloat(pending.ec_tc.toFixed(2)) : null,
      tempc:    pending.tempc   != null ? parseFloat(pending.tempc.toFixed(1))    : null,
      salinity: pending.salinity != null ? parseFloat(pending.salinity.toFixed(2)) : null,
      memo:     saveMemo.trim(),
    };
    saveSoilData([entry, ...soilData]);
    setPending(null);
    setSaveMemo("");
  };

  const deleteEntry = id => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    saveSoilData(soilData.filter(s => s.id !== id));
  };

  const bleConnected = bleStatus === "connected";

  return (
    <div className="slide-up" style={{ minHeight:"100vh", paddingBottom:80 }}>

      {/* ── 상단바: BLE 상태 */}
      <div style={{ position:"sticky", top:0, zIndex:50, background:"#0a0f0a",
        borderBottom:"1px solid #1a2e1a", padding:"13px 16px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <span style={{ flex:1, fontSize:14, fontWeight:700, color:"#f0fdf4",
            fontFamily:"'Noto Sans KR',sans-serif" }}>토양 측정</span>
          {bleConnected && battery !== null && (
            <span style={{ color:"#4ade80", fontSize:10, background:"#022c22",
              border:"1px solid #065f46", borderRadius:20, padding:"2px 7px" }}>🔋 {battery}%</span>
          )}
          {bleConnected && (
            <span style={{ color:"#6b7280", fontSize:10, background:"#0a0f0a",
              border:"1px solid #1a2e1a", borderRadius:20, padding:"2px 7px" }}>📡 {notifyCountRef.current}</span>
          )}
          <button onClick={() => setShowThreshold(v => !v)}
            style={{ padding:"7px 10px", background:"transparent",
              border:"1px solid #1a2e1a", borderRadius:8,
              color:"#9ca3af", cursor:"pointer", fontSize:16 }}>⚙️</button>
          {bleConnected ? (
            <button onClick={disconnectBLE}
              style={{ padding:"7px 12px", background:"#0c1a3b", border:"1px solid #3b82f6",
                borderRadius:8, color:"#60a5fa", cursor:"pointer", fontSize:12, fontWeight:600 }}>
              연결 해제
            </button>
          ) : (
            <button onClick={connectBLE} disabled={bleStatus==="connecting"}
              style={{ padding:"7px 14px",
                background:bleStatus==="connecting"?"#052e16":"#14532d",
                border:`1px solid ${bleStatus==="connecting"?"#166534":"#22c55e"}`,
                borderRadius:8, color:"#4ade80", cursor:"pointer", fontSize:12, fontWeight:600,
                opacity:bleStatus==="connecting"?0.7:1 }}>
              {bleStatus==="connecting" ? "연결중..." : "POGO 연결"}
            </button>
          )}
        </div>
        {bleStatus==="error" && (
          <p style={{ color:"#f87171", fontSize:11, marginTop:6 }}>연결 실패. 장치를 확인하고 다시 시도하세요.</p>
        )}
      </div>

      {/* ── 내부 탭: 목록 | 지도 */}
      <div style={{ display:"flex", borderBottom:"1px solid #1a2e1a" }}>
        {[{id:"list",label:"목록"},{id:"map",label:"지도"}].map(t => (
          <button key={t.id} onClick={()=>setInnerTab(t.id)}
            style={{ flex:1, padding:"11px 4px", background:"transparent", border:"none",
              cursor:"pointer", fontSize:13, fontWeight:innerTab===t.id?700:400,
              color:innerTab===t.id?"#4ade80":"#6b7280",
              borderBottom:`2px solid ${innerTab===t.id?"#4ade80":"transparent"}`,
              transition:"all .15s", fontFamily:"inherit" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── 수분 기준 설정 패널 */}
      {showThreshold && (
        <div style={{ padding:"12px 16px", background:"#0d150d",
          borderBottom:"1px solid #1a2e1a" }}>
          <p style={{ color:"#9ca3af", fontSize:12, marginBottom:10 }}>수분 기준 설정 (%)</p>
          <div style={{ display:"flex", gap:12, alignItems:"center" }}>
            {[
              { key:"dry",     label:"건조",  arrow:"↓", color:"#ef4444" },
              { key:"caution", label:"주의",  arrow:"↓", color:"#fbbf24" },
              { key:"wet",     label:"과습",  arrow:"↑", color:"#60a5fa" },
            ].map(item => (
              <div key={item.key} style={{ flex:1, textAlign:"center" }}>
                <p style={{ color:item.color, fontSize:11, marginBottom:4 }}>
                  {item.label} {item.arrow}
                </p>
                <input
                  type="number" min="1" max="99"
                  value={localThreshold[item.key]}
                  onChange={e => setLocalThreshold(prev => ({
                    ...prev, [item.key]: e.target.value
                  }))}
                  style={{ width:"100%", background:"#0a0f0a",
                    border:`1px solid ${item.color}`, borderRadius:8,
                    padding:"8px", color:item.color, fontSize:16,
                    fontWeight:700, textAlign:"center", outline:"none" }}
                />
              </div>
            ))}
            <button onClick={() => {
                const dry     = parseInt(localThreshold.dry);
                const caution = parseInt(localThreshold.caution);
                const wet     = parseInt(localThreshold.wet);
                if (isNaN(dry) || isNaN(caution) || isNaN(wet)) {
                  window.alert("올바른 숫자를 입력하세요");
                  return;
                }
                if (dry >= caution || caution >= wet) {
                  window.alert("건조 < 주의 < 과습 순서로 입력하세요");
                  return;
                }
                saveSoilThreshold({ dry, caution, wet });
                setShowThreshold(false);
              }}
              style={{ padding:"8px 10px", background:"#14532d",
                border:"1px solid #22c55e", borderRadius:8,
                color:"#4ade80", cursor:"pointer", fontSize:11,
                fontWeight:600, whiteSpace:"nowrap" }}>설정</button>
          </div>
          <p style={{ color:"#374151", fontSize:10, marginTop:8, textAlign:"center" }}>
            적정: {localThreshold.caution}% ~ {localThreshold.wet}% (자동)
          </p>
        </div>
      )}

      {/* ── 측정 버튼 (연결됐을 때, 목록탭) */}
      {bleConnected && innerTab==="list" && (
        <div style={{ padding:"12px 16px" }}>
          <button onClick={measure} disabled={measuring}
            style={{ width:"100%", padding:"16px",
              background:measuring?"#052e16":"#14532d",
              border:`2px solid ${measuring?"#166534":"#22c55e"}`,
              borderRadius:14, color:"#4ade80", cursor:"pointer", fontSize:16, fontWeight:700,
              display:"flex", alignItems:"center", justifyContent:"center", gap:10,
              boxShadow:"0 0 20px rgba(34,197,94,.15)", opacity:measuring?0.7:1,
              fontFamily:"inherit", transition:"all .2s" }}>
            {measuring ? "⏳ 측정중..." : "🌱 측정하기"}
          </button>
          {measuring && (
            <p style={{ color:"#4ade80", fontSize:10, textAlign:"center",
              marginTop:4, wordBreak:"break-all", padding:"0 16px" }}>
              {lastPacket || "대기중..."}
            </p>
          )}
        </div>
      )}

      {/* ── 측정 완료 저장 카드 */}
      {pending && (
        <div style={{ margin:"0 16px 12px" }}>
          <Card style={{ padding:16, border:"1px solid #22c55e" }}>
            <p style={{ color:"#4ade80", fontSize:12, fontWeight:700, marginBottom:12 }}>
              📊 측정 완료 — 위치/홀 지정 후 저장
            </p>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12 }}>
              {(() => {
                const ms = moistureStatus(pending.moisture, soilThreshold);
                return [
                  { label:"수분",    value:pending.moisture != null ? `${pending.moisture.toFixed(1)}%`   : "-",  ms },
                  { label:"EC",      value:pending.ec       != null ? `${pending.ec.toFixed(2)} dS/m`    : "-",  ms:null },
                  { label:"온도",    value:pending.tempc    != null ? `${pending.tempc.toFixed(1)}°C`     : "-",  ms:null },
                  { label:"염도",    value:pending.salinity != null ? `${pending.salinity.toFixed(2)}`    : "-",  ms:null },
                ].map(item => (
                  <div key={item.label} style={{ background:"#0a0f0a", borderRadius:8, padding:"10px 12px",
                    border:item.ms ? `1px solid ${item.ms.border}` : "1px solid #1a2e1a" }}>
                    <p style={{ color:"#6b7280", fontSize:12, marginBottom:2 }}>{item.label}</p>
                    <p style={{ color:item.ms ? item.ms.color : "#f0fdf4", fontSize:22, fontWeight:700 }}>
                      {item.value}
                      {item.ms && <span style={{ fontSize:11, marginLeft:4 }}>({item.ms.label})</span>}
                    </p>
                  </div>
                ));
              })()}
            </div>
            {pending.lat ? (
              <p style={{ color:"#4ade80", fontSize:11, marginBottom:10 }}>
                📍 {Number(pending.lat).toFixed(5)}, {Number(pending.lng).toFixed(5)}
              </p>
            ) : (
              <p style={{ color:"#6b7280", fontSize:11, marginBottom:10 }}>📍 GPS 없음</p>
            )}
            {hasCourse && (
              <div style={{ marginBottom:8 }}>
                <Sel value={saveCourse} onChange={e=>{setSaveCourse(e.target.value);setSaveHole("1");}}>
                  <option value="">코스 선택 안함</option>
                  {courses.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
                </Sel>
              </div>
            )}
            <Sel value={saveHole} onChange={e=>setSaveHole(e.target.value)}>
              {holeOpts.map(h=><option key={h} value={h}>{/^\d+$/.test(h)?`${h}번홀`:h}</option>)}
            </Sel>
            <textarea
              value={saveMemo}
              onChange={e=>setSaveMemo(e.target.value)}
              placeholder="메모 (선택사항)"
              rows={2}
              style={{ width:"100%", background:"#0d150d", border:"1px solid #1a2e1a",
                borderRadius:8, padding:"10px 14px", color:"#e2e8f0",
                fontSize:14, resize:"none", marginTop:8, boxSizing:"border-box" }}
            />
            <div style={{ display:"flex", gap:8, marginTop:12 }}>
              <Btn variant="ghost" style={{ flex:1 }} onClick={()=>setPending(null)}>취소</Btn>
              <Btn style={{ flex:2 }} onClick={saveMeasurement}>저장</Btn>
            </div>
          </Card>
        </div>
      )}

      {/* ── 목록 탭 */}
      {innerTab==="list" && (
        <div style={{ padding:"0 16px" }}>
          {soilData.length === 0 ? (
            <div style={{ paddingTop:60, textAlign:"center" }}>
              <p style={{ fontSize:40, marginBottom:16 }}>🌱</p>
              <p style={{ color:"#4b5563", fontSize:13, marginBottom:8 }}>측정 기록이 없습니다</p>
              <p style={{ color:"#374151", fontSize:11 }}>POGO를 연결하고 측정해 보세요</p>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {soilData.map(s => {
                const ms = moistureStatus(s.moisture, soilThreshold);
                return (
                  <Card key={s.id} style={{ padding:"12px 14px" }}>
                    <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between" }}>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6, flexWrap:"wrap" }}>
                          <span style={{ color:ms.color, fontSize:20, fontWeight:700 }}>{s.moisture?.toFixed(1)}%</span>
                          <span style={{ background:ms.bg, border:`1px solid ${ms.border}`,
                            color:ms.color, fontSize:12, padding:"2px 8px", borderRadius:20 }}>{ms.label}</span>
                          {s.course && <span style={{ color:"#e2e8f0", fontSize:13 }}>{s.course}</span>}
                          <span style={{ color:"#e2e8f0", fontSize:13 }}>
                            {/^\d+$/.test(s.hole)?`${s.hole}번홀`:s.hole}
                          </span>
                        </div>
                        <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
                          <span style={{ color:"#d1d5db", fontSize:13 }}>EC {s.ec?.toFixed(2)} dS/m</span>
                          <span style={{ color:"#d1d5db", fontSize:13 }}>온도 {s.tempc?.toFixed(1)}°C</span>
                          <span style={{ color:"#d1d5db", fontSize:13 }}>염도 {s.salinity?.toFixed(2)}</span>
                        </div>
                        <p style={{ color:"#9ca3af", fontSize:12, marginTop:4 }}>{s.date}</p>
                        {s.memo && (
                          <p style={{ color:"#d1d5db", fontSize:12, marginTop:4 }}>📝 {s.memo}</p>
                        )}
                      </div>
                      <button onClick={() => {
                          const shareText = [
                            `📍 ${s.course ? s.course + ' ' : ''}${/^\d+$/.test(s.hole)?s.hole+'번홀':s.hole}`,
                            `📅 ${s.date}`,
                            `💧 수분: ${s.moisture?.toFixed(1)}%`,
                            `⚡ EC: ${s.ec?.toFixed(2)} dS/m`,
                            `🌡 온도: ${s.tempc?.toFixed(1)}°C`,
                            `🧂 염도: ${s.salinity?.toFixed(2)}`,
                            s.lat ? `📌 GPS: ${Number(s.lat).toFixed(5)}, ${Number(s.lng).toFixed(5)}` : '',
                            s.memo ? `📝 메모: ${s.memo}` : '',
                          ].filter(Boolean).join('\n');
                          if (navigator.share) {
                            navigator.share({ title:'POGO 토양 측정', text:shareText }).catch(()=>{});
                          } else {
                            navigator.clipboard?.writeText(shareText).then(()=>{
                              window.alert('클립보드에 복사됐습니다');
                            });
                          }
                        }}
                        style={{ background:"transparent", border:"1px solid #1e40af", borderRadius:6,
                          padding:6, color:"#60a5fa", cursor:"pointer", width:28, height:28,
                          display:"flex", alignItems:"center", justifyContent:"center",
                          flexShrink:0, marginLeft:8 }}>
                        {Ic.share}
                      </button>
                      <button onClick={()=>deleteEntry(s.id)}
                        style={{ background:"transparent", border:"1px solid #450a0a", borderRadius:6,
                          padding:6, color:"#f87171", cursor:"pointer", width:28, height:28,
                          display:"flex", alignItems:"center", justifyContent:"center",
                          flexShrink:0, marginLeft:4 }}>
                        {Ic.trash}
                      </button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── 지도 탭 */}
      {innerTab==="map" && (
        <SoilMap soilData={soilData} activeGC={activeGC} soilThreshold={soilThreshold} />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
//  🌱 SOIL MAP
// ────────────────────────────────────────────────────────────────
function SoilMap({ soilData, activeGC, soilThreshold }) {
  const [sdkReady, setSdkReady] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [selected, setSelected] = useState(null);
  const mapRef      = useRef(null);
  const mapObj      = useRef(null);
  const overlaysRef = useRef([]);
  const selectedRef = useRef(null);
  const [mapType, setMapType] = useState("normal");
  selectedRef.current = setSelected;

  const withGps = soilData.filter(s => s.lat && s.lng);

  useEffect(() => {
    if (window.kakao && window.kakao.maps) { setSdkReady(true); return; }
    const js = document.createElement("script");
    js.src = "//dapi.kakao.com/v2/maps/sdk.js?appkey=24351eec6a145d3f320f47e61984f69a&autoload=false&libraries=services";
    js.onload  = () => window.kakao.maps.load(() => setSdkReady(true));
    js.onerror = () => setSdkReady(true);
    document.head.appendChild(js);
  }, []);

  useEffect(() => {
    if (!sdkReady || !mapRef.current) return;
    const kakao = window.kakao;
    if (!kakao) return;
    const first = withGps[0];
    let cLat = 37.2980, cLng = 127.6372, cLv = 5;
    if (activeGC?.lat)      { cLat = Number(activeGC.lat); cLng = Number(activeGC.lng); cLv = 4; }
    else if (first)         { cLat = Number(first.lat);    cLng = Number(first.lng);    cLv = 4; }
    setTimeout(() => {
      const map = new kakao.maps.Map(mapRef.current, {
        center: new kakao.maps.LatLng(cLat, cLng),
        level:  cLv,
      });
      mapObj.current = map;
      kakao.maps.event.addListener(map, "click", () => selectedRef.current(null));
      setMapReady(true);
    }, 100);
  }, [sdkReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mapReady || !mapObj.current) return;
    const kakao = window.kakao;
    if (!kakao) return;
    const map = mapObj.current;
    overlaysRef.current.forEach(o => o.setMap(null));
    overlaysRef.current = [];
    withGps.forEach(s => {
      const color = moistureStatus(s.moisture, soilThreshold).color;
      const div = document.createElement('div');
      div.style.cssText = 'cursor:pointer';
      div.innerHTML = `<div style="width:20px;height:20px;background:${color};border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,.5)"></div>`;
      div.addEventListener('click', (ev) => {
        ev.stopPropagation();
        selectedRef.current(s);
      });
      const overlay = new kakao.maps.CustomOverlay({
        position: new kakao.maps.LatLng(Number(s.lat), Number(s.lng)),
        content: div,
        yAnchor: 1,
      });
      overlay.setMap(map);
      overlaysRef.current.push(overlay);
    });
  }, [mapReady, soilData]); // eslint-disable-line react-hooks/exhaustive-deps

  const ms = selected ? moistureStatus(selected.moisture, soilThreshold) : null;

  const goToMyLocation = () => {
    if (!mapObj.current) return;
    navigator.geolocation?.getCurrentPosition(
      p => {
        const ll = new window.kakao.maps.LatLng(p.coords.latitude, p.coords.longitude);
        mapObj.current.setCenter(ll);
        mapObj.current.setLevel(3);
        new window.kakao.maps.CustomOverlay({
          position: ll,
          content: `<div style="width:16px;height:16px;background:#3b82f6;border:3px solid white;border-radius:50%;box-shadow:0 0 0 4px rgba(59,130,246,.3)"></div>`,
          yAnchor: 0.5,
        }).setMap(mapObj.current);
      },
      () => window.alert("GPS 권한을 허용해 주세요"),
      { enableHighAccuracy: true }
    );
  };

  return (
    <div style={{ position:"relative", height:"calc(100vh - 170px)" }}>
      {!sdkReady && (
        <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center",
          justifyContent:"center", background:"#0a0f0a" }}>
          <p style={{ color:"#4ade80", fontSize:14 }}>지도 로딩중...</p>
        </div>
      )}
      <div ref={mapRef} style={{ width:"100%", height:"100%" }} />

      {/* 지도 타입 토글 + 내 위치 */}
      <div style={{ position:"absolute", top:10, left:10, zIndex:1000, display:"flex", gap:4 }}>
        <button onClick={goToMyLocation}
          style={{ padding:"6px 10px", fontSize:12, borderRadius:8,
            background:"rgba(10,15,10,.9)", color:"#4ade80",
            border:"none", cursor:"pointer",
            boxShadow:"0 2px 6px rgba(0,0,0,.4)",
            display:"flex", alignItems:"center", gap:4 }}>
          <span style={{ width:16, height:16 }}>{Ic.gps}</span>
        </button>
        {[{key:"normal",label:"일반"},{key:"skyview",label:"위성"}].map(t => (
          <button key={t.key} onClick={()=>{
            setMapType(t.key);
            if (mapObj.current && window.kakao) {
              mapObj.current.setMapTypeId(
                t.key === "normal"
                  ? window.kakao.maps.MapTypeId.ROADMAP
                  : window.kakao.maps.MapTypeId.HYBRID
              );
            }
          }}
            style={{ padding:"6px 12px", fontSize:12, borderRadius:8, cursor:"pointer",
              fontFamily:"inherit", fontWeight:600, border:"none",
              background:mapType===t.key?"#4ade80":"rgba(10,15,10,.9)",
              color:mapType===t.key?"#052e16":"#9ca3af",
              boxShadow:"0 2px 6px rgba(0,0,0,.4)" }}>{t.label}</button>
        ))}
      </div>      

      {/* 범례 */}
      <div style={{ position:"absolute", top:10, right:10, zIndex:1000,
        background:"rgba(10,15,10,.92)", border:"1px solid #1a2e1a",
        borderRadius:10, padding:"10px 12px", display:"flex", flexDirection:"column", gap:5 }}>
        {[{label:"건조",color:"#ef4444"},{label:"주의",color:"#fbbf24"},
          {label:"적정",color:"#4ade80"},{label:"과습",color:"#60a5fa"}].map(item => (
          <div key={item.label} style={{ display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ width:10, height:10, background:item.color, borderRadius:"50%" }} />
            <span style={{ color:"#9ca3af", fontSize:11 }}>{item.label}</span>
          </div>
        ))}
      </div>

      {/* 선택된 마커 상세 */}
      {selected && ms && (
        <div style={{ position:"absolute", bottom:16, left:16, right:16, zIndex:1000 }}>
          <Card style={{ padding:"14px 16px", border:`1px solid ${ms.border}`,
            boxShadow:"0 4px 20px rgba(0,0,0,.6)" }}>
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between" }}>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                  <span style={{ color:ms.color, fontSize:18, fontWeight:700 }}>{selected.moisture}%</span>
                  <span style={{ background:ms.bg, border:`1px solid ${ms.border}`,
                    color:ms.color, fontSize:11, padding:"2px 8px", borderRadius:20 }}>{ms.label}</span>
                </div>
                <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:4 }}>
                  <span style={{ color:"#4b5563", fontSize:12 }}>EC {selected.ec} dS/m</span>
                  <span style={{ color:"#4b5563", fontSize:12 }}>{selected.tempc}°C</span>
                  <span style={{ color:"#4b5563", fontSize:12 }}>염도 {selected.salinity}</span>
                </div>
                <p style={{ color:"#374151", fontSize:11 }}>
                  {selected.course && `${selected.course} · `}
                  {/^\d+$/.test(selected.hole)?`${selected.hole}번홀`:selected.hole} · {selected.date}
                </p>
              </div>
              <button onClick={()=>setSelected(null)}
                style={{ background:"transparent", border:"1px solid #1a2e1a", borderRadius:8,
                  padding:"6px 10px", color:"#6b7280", cursor:"pointer", fontSize:12, marginLeft:10 }}>✕</button>
            </div>
          </Card>
        </div>
      )}

      {withGps.length === 0 && mapReady && (
        <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", zIndex:1000 }}>
          <Card style={{ padding:20, textAlign:"center" }}>
            <p style={{ color:"#4b5563", fontSize:13 }}>GPS 기록된 측정 데이터가 없습니다</p>
          </Card>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  ⛳ GOLF COURSE LIST SCREEN
// ════════════════════════════════════════════════════════════════
function GolfCourseListScreen({ golfCourses, saveGCs, activeGCId, saveActiveGCId, push, pop }) {
  const deleteGC = (id) => {
    if (!window.confirm("골프장을 삭제하시겠습니까?")) return;
    const next = golfCourses.filter(gc => gc.id !== id);
    saveGCs(next);
    if (activeGCId === id) saveActiveGCId(next[0]?.id || null);
  };

  return (
    <div className="slide-up" style={{ paddingBottom:80, minHeight:"100vh" }}>
      <TopBar title="골프장 관리" onBack={pop} right={
        <Btn onClick={()=>push("gcForm", { gc:null })} style={{ padding:"7px 12px", fontSize:13, gap:4 }}>
          <span style={{ width:16, height:16 }}>{Ic.plus}</span> 추가
        </Btn>
      } />
      <div style={{ padding:16, display:"flex", flexDirection:"column", gap:10 }}>

        {golfCourses.length === 0 ? (
          <div style={{ paddingTop:60, textAlign:"center" }}>
            <p style={{ fontSize:40, marginBottom:16 }}>⛳</p>
            <p style={{ color:"#9ca3af", fontSize:14, marginBottom:8 }}>등록된 골프장이 없습니다</p>
            <Btn onClick={()=>push("gcForm", { gc:null })}>첫 골프장 등록하기</Btn>
          </div>
        ) : (
          golfCourses.map(gc => {
            const isActive = gc.id === activeGCId;
            return (
              <Card key={gc.id} style={{ padding:"14px 16px",
                border:`1px solid ${isActive ? "#22c55e" : "#1a2e1a"}`,
                background: isActive ? "#052e16" : "#0d150d" }}>
                <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                  {/* 활성화 버튼 */}
                  <button onClick={()=>saveActiveGCId(gc.id)}
                    style={{ width:28, height:28, borderRadius:"50%", flexShrink:0, cursor:"pointer",
                      border:`2px solid ${isActive ? "#22c55e" : "#374151"}`,
                      background: isActive ? "#22c55e" : "transparent",
                      display:"flex", alignItems:"center", justifyContent:"center" }}>
                    {isActive && <span style={{ color:"#052e16", fontSize:14, fontWeight:900 }}>✓</span>}
                  </button>

                  {/* 골프장 정보 */}
                  <div style={{ flex:1, cursor:"pointer" }} onClick={()=>push("gcForm", { gc })}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
                      <p style={{ color: isActive ? "#4ade80" : "#f0fdf4", fontSize:16, fontWeight:700 }}>{gc.name}</p>
                      {isActive && (
                        <span style={{ background:"#14532d", border:"1px solid #22c55e", color:"#4ade80",
                          fontSize:10, padding:"1px 7px", borderRadius:20, fontWeight:600 }}>활성</span>
                      )}
                    </div>
                    <p style={{ color:"#4b5563", fontSize:12 }}>
                      {gc.courses?.length > 0
                        ? gc.courses.map(c=>c.name).join(" · ")
                        : "코스 미등록"}
                      {gc.lat && <span style={{ color:"#374151" }}> · GPS✓</span>}
                    </p>
                  </div>

                  {/* 편집/삭제 버튼 */}
                  <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                    <button onClick={()=>push("gcForm", { gc })}
                      style={{ background:"transparent", border:"1px solid #1a2e1a", borderRadius:6,
                        padding:6, color:"#9ca3af", cursor:"pointer", width:30, height:30,
                        display:"flex", alignItems:"center", justifyContent:"center" }}>
                      {Ic.edit}
                    </button>
                    <button onClick={()=>deleteGC(gc.id)}
                      style={{ background:"transparent", border:"1px solid #450a0a", borderRadius:6,
                        padding:6, color:"#f87171", cursor:"pointer", width:30, height:30,
                        display:"flex", alignItems:"center", justifyContent:"center" }}>
                      {Ic.trash}
                    </button>
                  </div>
                </div>
              </Card>
            );
          })
        )}

        <p style={{ color:"#374151", fontSize:11, textAlign:"center", marginTop:8 }}>
          ✓ 표시된 골프장이 지도·장비등록의 기준이 됩니다
        </p>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  ⛳ GOLF COURSE FORM SCREEN (생성 / 수정)
// ════════════════════════════════════════════════════════════════
function GolfCourseFormScreen({ golfCourses, saveGCs, saveActiveGCId, gc:initGC, pop }) {
  const isEdit = !!initGC;
  const [f, setF] = useState({
    id: uid(),
    courses: [],
    ...DEFAULT_GC,
    ...(initGC || {}),
  });
  const set = (k, v) => setF(p => ({ ...p, [k]:v }));

  const [gpsLoading,  setGpsLoading]  = useState(false);
  const [addrLoading, setAddrLoading] = useState(false);
  const [msg,         setMsg]         = useState("");

  // 코스 추가 폼
  const [newCourseName,  setNewCourseName]  = useState("");
  const [newCourseHoles, setNewCourseHoles] = useState(18);
  const [addingCourse,   setAddingCourse]   = useState(false);

  const captureGPS = () => {
    setGpsLoading(true);
    navigator.geolocation?.getCurrentPosition(
      p => {
        setF(prev => ({ ...prev, lat:p.coords.latitude.toFixed(6), lng:p.coords.longitude.toFixed(6) }));
        setGpsLoading(false);
        setMsg("✓ 현재 위치가 지도 중심으로 설정됩니다");
        setTimeout(() => setMsg(""), 3000);
      },
      () => { window.alert("GPS를 사용할 수 없습니다"); setGpsLoading(false); },
      { enableHighAccuracy:true }
    );
  };

  const searchAddress = () => {
    if (!f.address.trim()) return;
    setAddrLoading(true);
    const load = (attempt = 0) => {
      const kakao = window.kakao;
      // services 로드 대기 (최대 10초)
      if (!kakao || !kakao.maps || !kakao.maps.services) {
        if (attempt > 33) {
          setAddrLoading(false);
          setMsg("주소 검색 서비스를 불러올 수 없습니다. 잠시 후 다시 시도해 주세요.");
          setTimeout(() => setMsg(""), 5000);
          return;
        }
        return setTimeout(() => load(attempt + 1), 300);
      }
      const geocoder = new kakao.maps.services.Geocoder();
      geocoder.addressSearch(f.address, (result, status) => {
        setAddrLoading(false);
        if (status === kakao.maps.services.Status.OK) {
          setF(prev => ({ ...prev, lat:Number(result[0].y).toFixed(6), lng:Number(result[0].x).toFixed(6) }));
          setMsg("✓ 주소로 좌표를 설정했습니다");
          setTimeout(() => setMsg(""), 3000);
        } else {
          setMsg("주소를 찾을 수 없습니다. 직접 좌표를 입력해 주세요.");
          setTimeout(() => setMsg(""), 5000);
        }
      });
    };
    if (!window.kakao || !window.kakao.maps) {
      const js = document.createElement("script");
      js.src = "//dapi.kakao.com/v2/maps/sdk.js?appkey=24351eec6a145d3f320f47e61984f69a&autoload=false&libraries=services";
      js.onload = () => window.kakao.maps.load(load);
      document.head.appendChild(js);
    } else load();
  };

  const addCourse = () => {
    if (!newCourseName.trim()) return window.alert("코스 이름을 입력하세요");
    if (f.courses.some(c => c.name === newCourseName.trim())) return window.alert("같은 이름의 코스가 이미 있습니다");
    const course = { id:uid(), name:newCourseName.trim(), holes:Number(newCourseHoles)||18 };
    setF(prev => ({ ...prev, courses:[...prev.courses, course] }));
    setNewCourseName(""); setNewCourseHoles(18); setAddingCourse(false);
  };

  const removeCourse = (id) => {
    if (!window.confirm("코스를 삭제하시겠습니까?")) return;
    setF(prev => ({ ...prev, courses:prev.courses.filter(c => c.id !== id) }));
  };

  const save = () => {
    if (!f.name.trim()) return window.alert("골프장 이름을 입력하세요");
    if (isEdit) {
      saveGCs(golfCourses.map(gc => gc.id === f.id ? f : gc));
    } else {
      saveGCs([...golfCourses, f]);
      saveActiveGCId(f.id);
    }
    pop();
  };

  const clear = () => {
    if (!window.confirm("이 골프장 정보를 초기화하시겠습니까?")) return;
    setF(prev => ({ id:prev.id, courses:[], ...DEFAULT_GC }));
  };

  return (
    <div className="slide-up" style={{ paddingBottom:80, minHeight:"100vh" }}>
      <TopBar title={isEdit ? "골프장 수정" : "골프장 등록"} onBack={pop} />
      <div style={{ padding:16, display:"flex", flexDirection:"column", gap:16 }}>

        <Inp label="골프장 이름 *" placeholder="예: 솔모로CC, 황금나무GC" value={f.name} onChange={e=>set("name",e.target.value)} />

        {/* 주소 검색 */}
        <div>
          <label style={{ display:"block", color:"#6b7280", fontSize:12, marginBottom:5, letterSpacing:".04em" }}>주소 (좌표 자동 검색용)</label>
          <div style={{ display:"flex", gap:8 }}>
            <input value={f.address||""} onChange={e=>set("address",e.target.value)}
              placeholder="예: 경기도 여주시 가남읍 솔모로그린길 171"
              style={{ flex:1, background:"#0d150d", border:"1px solid #1a2e1a", borderRadius:8,
                padding:"10px 14px", color:"#e2e8f0", fontFamily:"inherit", fontSize:13, outline:"none" }}
              onFocus={e=>e.target.style.borderColor="#4ade80"}
              onBlur={e=>e.target.style.borderColor="#1a2e1a"} />
            <Btn variant="blue" style={{ padding:"10px 14px", fontSize:12, flexShrink:0, whiteSpace:"nowrap" }}
              onClick={searchAddress} disabled={addrLoading}>
              {addrLoading ? "검색중..." : "주소 검색"}
            </Btn>
          </div>
        </div>

        {/* 지도 중심 좌표 */}
        <Card style={{ padding:14 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
            <label style={{ color:"#6b7280", fontSize:12, letterSpacing:".04em", display:"flex", alignItems:"center", gap:6 }}>
              <span style={{ width:14, height:14, color:"#4ade80" }}>{Ic.gps}</span> 지도 중심 좌표
            </label>
            <Btn variant="green" style={{ padding:"6px 12px", fontSize:12 }} onClick={captureGPS} disabled={gpsLoading}>
              {gpsLoading ? "확인중..." : "현재 위치"}
            </Btn>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            <Inp placeholder="위도 (Lat)" value={f.lat||""} onChange={e=>set("lat",e.target.value)} />
            <Inp placeholder="경도 (Lng)" value={f.lng||""} onChange={e=>set("lng",e.target.value)} />
          </div>
          {f.lat && f.lng && (
            <p style={{ color:"#4ade80", fontSize:11, marginTop:8, textAlign:"center" }}>
              ✓ {Number(f.lat).toFixed(5)}, {Number(f.lng).toFixed(5)}
            </p>
          )}
          {msg && (
            <p style={{ color:msg.startsWith("✓")?"#4ade80":"#fbbf24", fontSize:12, marginTop:8,
              background:msg.startsWith("✓")?"#022c22":"#1c1003",
              border:`1px solid ${msg.startsWith("✓")?"#065f46":"#92400e"}`,
              borderRadius:6, padding:"6px 10px" }}>{msg}</p>
          )}
        </Card>

        {/* ── 코스 관리 */}
        <div>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
            <label style={{ color:"#6b7280", fontSize:12, letterSpacing:".04em" }}>
              코스 목록 ({f.courses.length}개)
            </label>
            {!addingCourse && (
              <Btn variant="green" style={{ padding:"6px 12px", fontSize:12, gap:4 }} onClick={()=>setAddingCourse(true)}>
                <span style={{ width:14, height:14 }}>{Ic.plus}</span> 코스 추가
              </Btn>
            )}
          </div>

          {addingCourse && (
            <Card style={{ padding:14, marginBottom:10, border:"1px solid #065f46" }}>
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                <Inp placeholder="코스 이름 (예: 파인코스, 레이크코스)"
                  value={newCourseName} onChange={e=>setNewCourseName(e.target.value)} />
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <label style={{ color:"#6b7280", fontSize:12, flexShrink:0 }}>홀 수</label>
                  <div style={{ display:"flex", gap:6 }}>
                    {[9, 18, 27, 36].map(n => (
                      <button key={n} onClick={()=>setNewCourseHoles(n)}
                        style={{ padding:"6px 14px", borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:600,
                          background:newCourseHoles===n?"#14532d":"#0d150d",
                          border:`1px solid ${newCourseHoles===n?"#22c55e":"#1a2e1a"}`,
                          color:newCourseHoles===n?"#4ade80":"#4b5563" }}>{n}홀</button>
                    ))}
                  </div>
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <Btn variant="ghost" style={{ flex:1 }} onClick={()=>{setAddingCourse(false);setNewCourseName("");setNewCourseHoles(18);}}>취소</Btn>
                  <Btn style={{ flex:2 }} onClick={addCourse}>추가</Btn>
                </div>
              </div>
            </Card>
          )}

          {f.courses.length > 0 ? (
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {f.courses.map((c, i) => (
                <Card key={c.id} style={{ padding:"12px 14px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ width:28, height:28, background:"#052e16", border:"1px solid #166534",
                      borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center",
                      color:"#4ade80", fontSize:12, fontWeight:700 }}>{i+1}</div>
                    <div>
                      <p style={{ color:"#f0fdf4", fontSize:14, fontWeight:600 }}>{c.name}</p>
                      <p style={{ color:"#4b5563", fontSize:11, marginTop:2 }}>{c.holes}홀</p>
                    </div>
                  </div>
                  <button onClick={()=>removeCourse(c.id)}
                    style={{ background:"transparent", border:"1px solid #450a0a", borderRadius:6,
                      padding:6, color:"#f87171", cursor:"pointer", width:28, height:28,
                      display:"flex", alignItems:"center", justifyContent:"center" }}>
                    {Ic.trash}
                  </button>
                </Card>
              ))}
            </div>
          ) : (
            <Card style={{ padding:16, textAlign:"center", border:"1px dashed #1a2e1a" }}>
              <p style={{ color:"#374151", fontSize:12 }}>등록된 코스가 없습니다</p>
              <p style={{ color:"#4b5563", fontSize:11, marginTop:4 }}>코스를 추가하면 장비 등록 시 코스별로 구분할 수 있습니다</p>
            </Card>
          )}
        </div>

        <div style={{ display:"flex", gap:10 }}>
          <Btn variant="danger" style={{ padding:"10px 14px", fontSize:13 }} onClick={clear}>초기화</Btn>
          <Btn variant="ghost" style={{ flex:1 }} onClick={pop}>취소</Btn>
          <Btn style={{ flex:2 }} onClick={save}>저장</Btn>
        </div>
      </div>
    </div>
  );
}